'use client'

import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react'
import { notFound, usePathname, useRouter, useSearchParams } from 'next/navigation'
import { addChatAtom } from '@/atoms/workspaceAtoms'
import { useAuth } from '@/context/auth'
import { ChatbotUIChatContext, type ScopedChatBindings } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { getAssistantToolsByAssistantId } from '@/db/assistant-tools'
import { getChatFilesByChatId } from '@/db/chat-files'
import { getFileByHashId } from '@/db/files'
import { getMessageFileItemsByMessageId } from '@/db/message-file-items'
import { getMessageById, getMessagesByChatId } from '@/db/messages'
import { getPromptById } from '@/db/prompts'
import { getMessageImageFromStorage } from '@/db/storage/message-images'
import { getToolById } from '@/db/tools'
import { Tables } from '@/supabase/types'
import type { ChatUnreadActivityFields } from '@/db/chat-members'
import { ChatAdjacentPaneContext, ChatMessage, LLM, LLMID, MessageImage } from '@/types'
import { ChatSendOptions, ChatStopOptions } from '@/types/chat'
import type { SidecarAgentState, SidecarChatStatus, SidecarRunState } from '@/types/sidecar'
import {
  IconArrowDown,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpandFilled,
  IconMessagePlus
} from '@tabler/icons-react'
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion'
import { useTheme } from 'next-themes'
import { Virtualizer } from 'virtua'
import { toast } from 'sonner'

import { CodeBlock } from '@/types/chat-message'
import { convertBlobToBase64 } from '@/lib/blob-to-b64'
import {
  CHAT_LATEST_SCROLL_REQUEST_EVENT,
  consumeChatLatestScrollRequest
} from '@/lib/chat/client-activity-registry'
import { sortChatMessagesByThreadOrder } from '@/lib/chat/message-order'
import { dedupeChatMessagesByIdentity } from '@/lib/chat/message-persistence-merge'
import { getGroupChatResponseMode } from '@/lib/chat/group-chat-response-mode'
import { getLatestPendingAssistantTurnState, completePendingAssistantTurnMetadata, stopStalePendingAssistantTurns } from '@/lib/chat/pending-assistant-turn'
import { reconcileMessage } from '@/lib/chat/message-reconciler'
import {
  bindCrewChatToNextChat,
  bindCrewMemberToNextChat,
  consumeCrewChatLaunch,
  CREW_CHAT_LAUNCH_PARAM,
  getCrewChatRosterForChat,
  getCrewMemberForChat
} from '@/lib/crew/crew-chat-binding'
import { parseCrewChatActiveSpeakerMateId } from '@/lib/crew/crew-chat'
import { CrewMemberChrome } from '@/components/crew/crew-member-chrome'
import {
  ChatRouteAuthError,
  ChatRouteForbiddenError,
  isChatRouteAuthError,
  isChatRouteForbiddenError
} from '@/lib/chat/chat-route-access-error'
import { resolveSurfaceSelectedChat } from '@/lib/chat/surface-selected-chat'
import {
  mergeRealtimeFileItems,
  mergeRealtimeMessageImages,
  removeRealtimeChatMessage,
  upsertParsedRealtimeChatMessage,
  upsertRealtimeChatMessage
} from '@/lib/chat/thread-realtime-state'
import useHotkey from '@/lib/hooks/use-hotkey'
import {
  ChatThreadMessageRealtimeEvent,
  useChatThreadRealtime
} from '@/lib/hooks/useChatThreadRealtime'
import {
  parseChatMessageCodeBlocksAndContent,
  parseDBMessageCodeBlocksAndContent
} from '@/lib/messages'
import { isMobileScreen } from '@/lib/mobile'
import { parseIdFromSlug } from '@/lib/slugify'
import { patchSidebarChatSnapshots } from '@/lib/sidebar/sidebar-chat-store'
import { notifySidebarChatUnreadActivityCleared } from '@/lib/sidebar/sidebar-mutation-events'
import { cn } from '@/lib/utils'
import {
  emitConnectorConnected,
  type ConnectorConnectedEventDetail
} from '@/lib/connectors/events'
import type { LosiConnectorType } from '@/lib/connectors/types'
import { safeConsole } from '@/lib/utils/safeConsole'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { buildLoginPath } from '@/lib/auth/redirect-path'
import { extractGeneratedChatImages } from '@/lib/chat/generated-images'
import { extractChatCreatedArtifacts } from '@/lib/chat/created-artifacts'
import { buildStoredAssetsZip, triggerBlobDownload } from '@/lib/download/client-zip'
import { Brand } from '@/components/ui/brand'
import { ImageModal } from '@/components/ui/image-modal'
import ImageLoading from '@/components/ui/image-loading'
import { Loading } from '@/components/ui/loading'
import { AssistantIcon } from '@/components/assistants/assistant-icon'
import { useChatHandler } from '@/components/chat/chat-hooks/use-chat-handler'
import { usePromptAndCommand } from '@/components/chat/chat-hooks/use-prompt-and-command'
import { useScopedSetUserInput } from '@/components/chat/chat-hooks/use-scoped-user-input'
import { ChatMessageCounter } from '@/components/chat/chat-message-counter'
import { ChatLongMessageNotice } from '@/components/chat/chat-long-message-notice'
import { ChatSettings } from '@/components/chat/chat-settings'
import { ShareChatButton } from '@/components/chat/chat-share-button'
import { ChatOverflowMenu } from '@/components/chat/chat-overflow-menu'
import { StarredChatButton } from '@/components/chat/chat-starred-button'
import { useBetaFeatureEnabled } from '@/hooks/use-beta-feature-access'
import { CANVAS_BROWSER_BETA_FEATURE_KEY } from '@/lib/beta-features/registry'
import { ChatHighlightsButton } from '@/components/chat/chat-highlights-button'
import { ChatGroupButton } from '@/components/chat/chat-group-button'
import {
  ChatCreatedStackPopover,
  type ChatCreatedArtifact
} from '@/components/chat/chat-created-stack-popover'
import { type ImageStackImage } from '@/components/spaces/shared/image-stack-popover'
import {
  dispatchSidekickOpenSpaceItem,
  getSidekickSpaceItemHref
} from '@/lib/sidekick/space-item-context'
import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'
import { ConversationStarters } from '@/components/chat/conversation-starters'
import { EmptyChatWidgetsSection } from '@/components/chat/empty-chat-widgets'
import { OnboardingChecklist } from '@/components/discoverability/onboarding-checklist'
import {
  getEmptyChatWidgetsState,
  DEFAULT_EMPTY_CHAT_WIDGETS_STATE,
  parseEmptyChatWidgetsStateFromApi,
  filterEmptyChatWidgetsForNexusAccess,
  setEmptyChatWidgetsState,
  type EmptyChatWidgetsState
} from '@/lib/chat-empty-widgets-state'
import { SIDEBAR_ITEM_ICON_SIZE } from '@/components/sidebar/items/all/sidebar-item-icon'

import { Button } from '../ui/button'
import { ChatHelp } from './chat-help'
import { useScroll } from './chat-hooks/use-scroll'
import { ChatInput } from './chat-input'
import { FadingChatTitle } from './fading-chat-title'
import { ChatMessageSelector } from './chat-message-selector'
import { ChatMessages } from './chat-messages'
import { ChatMessagesMinimap, type MinimapMessage } from './chat-messages-minimap'
import { ChatMessagesVirtualized } from './chat-messages-virtualized'
import { ChatScrollButtons } from './chat-scroll-buttons'
import { ChatSearch } from './ChatSearch'
import { useTranslation } from 'react-i18next'
import { TabsList, TabsTrigger, TabsContent, Tabs } from '@/components/ui/tabs'
import { LanguageInitializer } from '../languageswitcher/LanguageInitializer'
import { useChatMessages } from '@/hooks/use-chat-messages'
import { useActiveNotificationView } from '@/hooks/use-active-notification-view'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useServerEvents } from '@/hooks/useServerEvents'
import { useCombinedConversationStarters } from '@/hooks/use-combined-conversation-starters'
import { useSidecarRun } from '@/hooks/use-sidecar-run'
import { captureSharedChatFollowUpException } from '@/lib/analytics/capture-client-exception'
import { trackPublicChatStarterPromptClicked } from '@/lib/analytics/activation'
import { buildSeedRunFromMessage } from '@/lib/sidecar-seed'
import { PublicChatHeader } from './public-chat-header'
import { useSetAtom } from 'jotai'
import {
  consumePendingDesktopHandoff,
  isDesktopHost,
  onPendingDesktopHandoff,
  readPendingDesktopHandoff,
  reportPendingDesktopHandoffResult
} from '@/lib/desktop-host'
import {
  consumePendingContentEngineChatHandoff,
  onPendingContentEngineChatHandoff,
  readPendingContentEngineChatHandoff
} from '@/lib/chat/content-engine-handoff'
import {
  getQueuedSidecarTurnReplayBatch,
  getQueueableChatSendOptions,
  type QueuedSidecarTurnReplayBatch,
  type QueuedSidecarTurn
} from '@/lib/chat/sidecar-turn-queue'
import {
  hasQueuedDraftAttachments,
  removeQueuedDraftAttachments
} from '@/lib/chat/queued-attachments'
import {
  deleteChatHighlight,
  fetchChatHighlights,
  readCachedChatHighlights,
  saveChatHighlight,
  writeCachedChatHighlights,
  type ChatHighlight,
  type ChatHighlightDraft
} from '@/lib/chat/chat-highlights'
import type { ChatProductCreationCommand } from '@/lib/chat/product-creation'
import { usePrewarmChat } from '@/hooks/usePrewarmChat'

const iconProps = {
  size: SIDEBAR_ITEM_ICON_SIZE,
  stroke: 1.5,
  className: 'text-muted-foreground'
}

// Default conversation starters for all chats (used when no user context available)
const DEFAULT_CONVERSATION_STARTERS = [
  'What can you help me create with your tools?',
  'How do I automate a repetitive task in my workflow?',
  'Can you generate an image or video for my project?',
  'Help me research current information on a topic',
  'Can you help me write something?',
  'How do I solve complex problems step by step?',
  "What's something interesting I should know about?",
  'Help me learn something new today'
]
const INITIAL_VISIBLE_MESSAGE_LIMIT = 50
const INITIAL_MESSAGE_READ_LIMIT = INITIAL_VISIBLE_MESSAGE_LIMIT + 1
const SESSION_EXPIRED_TOAST_ID = 'chat-session-expired'
// A realtime change that arrives while isGenerating/isPersisting is true gets
// deferred until those flags clear (see the reconciliation effect below).
// The refresh happens when generation naturally completes — no forced timeout.

async function readChatApiError(response: Response, fallback: string): Promise<string> {
  const text = await response.text().catch(() => '')

  if (!text) {
    return fallback
  }

  try {
    const payload = JSON.parse(text) as { error?: string }
    return payload.error || fallback
  } catch {
    return text
  }
}

async function fetchAccessibleChatById(chatId: string): Promise<Tables<'chats'> | null> {
  const response = await authenticatedFetch(`/api/chat/${encodeURIComponent(chatId)}`, {
    cache: 'no-store'
  })

  if (response.status === 401) {
    throw new ChatRouteAuthError(
      await readChatApiError(response, 'Authentication required')
    )
  }

  if (response.status === 403) {
    throw new ChatRouteForbiddenError(
      await readChatApiError(response, 'Chat access denied')
    )
  }

  if (response.status === 404) {
    return null
  }

  if (!response.ok) {
    throw new Error(await readChatApiError(response, 'Failed to load chat'))
  }

  return (await response.json()) as Tables<'chats'>
}

async function fetchMessageImages(messages: Tables<'messages'>[]): Promise<MessageImage[]> {
  const imagePromises = messages.flatMap((message) =>
    Array.isArray(message.image_paths)
      ? message.image_paths.map(async (imagePath) => {
          const url = await getMessageImageFromStorage(imagePath)
          if (url) {
            const response = await fetch(url)
            const blob = await response.blob()
            const base64 = await convertBlobToBase64(blob)
            return {
              messageId: message.id,
              path: imagePath,
              base64,
              url,
              file: null
            }
          }
          return {
            messageId: message.id,
            path: imagePath,
            base64: '',
            url,
            file: null
          }
        })
      : []
  )

  return Promise.all(imagePromises)
}

function mergeImageStackImages(
  firstImages: ImageStackImage[],
  secondImages: ImageStackImage[]
) {
  const seenUrls = new Set<string>()
  const mergedImages: ImageStackImage[] = []

  for (const image of [...firstImages, ...secondImages]) {
    if (seenUrls.has(image.url)) {
      continue
    }

    seenUrls.add(image.url)
    mergedImages.push(image)
  }

  return mergedImages
}

async function readGeneratedImagesResponse(response: Response) {
  const payload = (await response.json().catch(() => null)) as
    | { images?: ImageStackImage[] }
    | null

  return Array.isArray(payload?.images) ? payload.images : []
}

function isOptimisticChatMessage(message: ChatMessage) {
  return (
    !message.message.created_at ||
    !message.message.chat_id ||
    !message.message.updated_at
  )
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function isDatabaseUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

function buildSeedRunFromSidecarStatus(
  status: SidecarChatStatus | null
): SidecarRunState | null {
  if (!status) return null

  return {
    runId: status.runId,
    chatId: status.chatId,
    status: status.status,
    objective: status.objective,
    message: status.message,
    startedAt: status.updatedAt,
    completedAt: status.completedAt,
    agentOrder: [],
    agents: {}
  }
}

function getLatestPersistedChatMessage(messages: ChatMessage[], chatId: string) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const chatMessage = messages[index]

    if (
      chatMessage &&
      !isOptimisticChatMessage(chatMessage) &&
      isDatabaseUuid(chatMessage.message.id) &&
      chatMessage.message.chat_id === chatId
    ) {
      return chatMessage.message
    }
  }

  return null
}

function clearUnreadActivity(chat: Tables<'chats'>) {
  const unreadChat = chat as Tables<'chats'> & ChatUnreadActivityFields

  if (!unreadChat.unread_activity) {
    return chat
  }

  return {
    ...chat,
    unread_activity: false,
    unread_activity_message_id: null,
    unread_activity_sequence_number: null
  } as Tables<'chats'>
}

function createHighlightId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  return `highlight-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function hasSameHighlightSelection(
  highlights: ChatHighlight[],
  candidate: Pick<
    ChatHighlight,
    'messageId' | 'text' | 'startOffset' | 'endOffset' | 'prefix' | 'suffix'
  >
) {
  return highlights.some(
    (highlight) =>
      highlight.messageId === candidate.messageId &&
      highlight.text === candidate.text &&
      highlight.startOffset === candidate.startOffset &&
      highlight.endOffset === candidate.endOffset &&
      highlight.prefix === candidate.prefix &&
      highlight.suffix === candidate.suffix
  )
}

function mergeHighlightsBySelection(
  primaryHighlights: ChatHighlight[],
  fallbackHighlights: ChatHighlight[]
) {
  const mergedHighlights = [...primaryHighlights]

  for (const highlight of fallbackHighlights) {
    if (!hasSameHighlightSelection(mergedHighlights, highlight)) {
      mergedHighlights.push(highlight)
    }
  }

  return mergedHighlights.sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  )
}

function mergeFetchedMessagesWithVisibleMessages(
  fetchedMessages: ChatMessage[],
  visibleMessages: ChatMessage[]
) {
  // The fetched thread is authoritative for which messages EXIST (server-side
  // deletions/reorders propagate). But per message, reconcile against the local
  // copy so a refetch that races a still-streaming turn can neither blank
  // in-flight content nor re-pin a resolved message to the loader (terminal
  // latch + LWW). Optimistic locals with no persisted match are preserved.
  const reconciledFetched = fetchedMessages.map((fetched) => {
    const local = visibleMessages.find(
      (visible) =>
        visible.message.id === fetched.message.id ||
        (visible.message.chat_id === fetched.message.chat_id &&
          visible.message.sequence_number === fetched.message.sequence_number)
    )

    return local ? reconcileMessage(local, fetched, 'refetch') : fetched
  })

  const optimisticMessages = visibleMessages.filter(isOptimisticChatMessage)

  const finalMessages =
    optimisticMessages.length === 0
      ? sortChatMessagesByThreadOrder(reconciledFetched)
      : (() => {
          const mergedMessages = [...reconciledFetched]
          const seenSequenceNumbers = new Set(
            reconciledFetched.map((message) => message.message.sequence_number)
          )

          optimisticMessages.forEach((message) => {
            if (seenSequenceNumbers.has(message.message.sequence_number)) {
              return
            }

            seenSequenceNumbers.add(message.message.sequence_number)
            mergedMessages.push(message)
          })

          return sortChatMessagesByThreadOrder(mergedMessages)
        })()

  // Collapse twin DB rows (same id or same chat+sequence+role) so refetch
  // never remounts two Message components with the same Losi steps/content.
  const dedupedFinalMessages = dedupeChatMessagesByIdentity(finalMessages)

  // Identity stability: if the reconciled result is element-wise identical to
  // what's already visible, return the ORIGINAL reference so the scoped-state
  // Object.is guard treats a steady-state refetch as a no-op (prevents the
  // realtime refetch cadence from driving an endless re-render loop / #185).
  if (
    dedupedFinalMessages.length === visibleMessages.length &&
    dedupedFinalMessages.every((message, index) => message === visibleMessages[index])
  ) {
    return visibleMessages
  }

  return dedupedFinalMessages
}

function syncPersistedAssistantTurnState(
  bindings: ScopedChatBindings,
  messages: ChatMessage[]
) {
  // Force-send / queued follow-ups can leave an older streamStatus:'running'
  // assistant above a newer user turn. Stop those before reading "latest".
  const withoutStale = stopStalePendingAssistantTurns(messages)
  if (withoutStale !== messages) {
    bindings.setChatMessages(withoutStale)
  }

  const pendingTurn = getLatestPendingAssistantTurnState(withoutStale)
  const snapshot = bindings.getSnapshot()

  const hasLiveAbortController = Boolean(
    snapshot.abortController &&
      !snapshot.abortController.signal.aborted
  )

  if (pendingTurn) {
    // Only mark as generating if there's an active stream (abort controller)
    // OR if we don't already have the response content locally for this turn.
    // This prevents a realtime refresh from re-entering generating state after
    // the local stream completed (stale DB metadata race), while still allowing
    // cross-tab/cross-device resume where the content hasn't loaded locally yet.
    const pendingMsgId = pendingTurn.message.message.id
    const localMessage = pendingMsgId
      ? snapshot.chatMessages.find(m => m.message.id === pendingMsgId)
      : null
    const hasLocalContent = localMessage &&
      typeof localMessage.message.content === 'string' &&
      localMessage.message.content.trim().length > 0

    if (hasLiveAbortController || !hasLocalContent) {
      if (!snapshot.isGenerating) {
        bindings.setIsGenerating(true)
      }
      // While the local stream owns the turn (abortController set), only RAISE
      // firstTokenReceived — never downgrade it from a lagging realtime echo,
      // which would re-arm the loader gate and (with rapid echoes) oscillate
      // the value, feeding a max-update-depth loop. Only write when it changes.
      const nextFirstToken = hasLiveAbortController
        ? snapshot.firstTokenReceived || pendingTurn.firstTokenReceived
        : pendingTurn.firstTokenReceived
      if (nextFirstToken !== snapshot.firstTokenReceived) {
        bindings.setFirstTokenReceived(nextFirstToken)
      }
      return
    }

    // Stale streamStatus:'running' metadata with local content and no active
    // abort — the turn already finished locally. Mark it completed and clear
    // generating so the next Send isn't blocked/queued until a second press.
    bindings.setChatMessages((previous) =>
      previous.map((chatMessage) => {
        if (chatMessage.message.id !== pendingMsgId) {
          return chatMessage
        }

        return {
          ...chatMessage,
          message: {
            ...chatMessage.message,
            metadata: completePendingAssistantTurnMetadata(
              chatMessage.message.metadata
            )
          }
        }
      })
    )
    if (snapshot.isGenerating) {
      bindings.setIsGenerating(false)
    }
    if (snapshot.isPersisting) {
      bindings.setIsPersisting(false)
    }
    if (snapshot.firstTokenReceived) {
      bindings.setFirstTokenReceived(false)
    }
    return
  }

  if (hasLiveAbortController) {
    return
  }

  // Drop aborted controllers that still sit on the store — they otherwise
  // look like a live stream and block queue replay / force-send forever.
  if (snapshot.abortController?.signal.aborted) {
    bindings.setAbortController(null)
  }

  if (snapshot.isGenerating) {
    bindings.setIsGenerating(false)
  }
  if (snapshot.firstTokenReceived) {
    bindings.setFirstTokenReceived(false)
  }
}

export type { QueuedSidecarTurn }

interface ChatUIProps {
  chatId?: string
  showModelSelector?: boolean
  showChatSettings?: boolean
  showAssistantSelector?: boolean
  assistant?: Tables<'assistants'>
  onSelectCodeBlock?: (codeBlock: CodeBlock | null) => void
  onSelectMessage?: (message: any) => void
  onChatCreate?: (chat: Tables<'chats'>) => void
  onChatUpdate?: (chat: Tables<'chats'>) => void
  experimentalCodeEditor: boolean // New prop
  forceDraftChat?: boolean
  sidecarEnabled?: boolean
  isPublic?: boolean // Public chat mode (no sidebar, public header)
  onNewChat?: () => void // Handler for new chat in public mode
  /** Plan mode: when user triggers Plan (e.g. Shift+Tab or Plan button), call with current message content */
  onPlanModeClick?: (messageContent: string) => void
  /** Whether a plan already exists for this chat (show "View plan" vs "Plan") */
  hasPlan?: boolean
  publicHeaderAuthAction?: React.ReactNode
  /** Public try-chat save prompt — sits under the last message, never over the composer. */
  publicInlineSaveCta?: React.ReactNode
  /** When provided, sidecar is shown in the shared canvas; opening the panel calls setPanelOpen(true). */
  sidecarCanvas?: {
    run: SidecarRunState | null
    runs: SidecarRunState[]
    selectRun: (runId: string, options?: { manual?: boolean }) => void
    panelOpen: boolean
    setPanelOpen: (open: boolean) => void
    queuedTurnsCount: number
    queuedTurnsArray: QueuedSidecarTurn[]
    setQueuedTurns: React.Dispatch<React.SetStateAction<QueuedSidecarTurn[]>>
  }
  /** When user clicks Sidecar entry (e.g. floating button) while panel may already be open: clear other canvas content so Sidecar is shown. */
  onRequestSidecarFocus?: () => void
  /** Hidden beta product creation command handler. */
  onProductCreationCommand?: (command: ChatProductCreationCommand) => void
  /** Whether this chat surface may move focus to its composer. */
  canFocusComposer?: boolean
  /** Optional context for the split-pane surface open beside this chat. */
  adjacentPaneContext?: ChatAdjacentPaneContext | null
}

export const ChatUI: React.FC<ChatUIProps> = ({
  showModelSelector = true,
  showChatSettings = true,
  showAssistantSelector = true,
  assistant,
  onChatCreate,
  onChatUpdate,
  onSelectCodeBlock,
  onSelectMessage,
  chatId,
  experimentalCodeEditor,
  forceDraftChat = false,
  sidecarEnabled = true,
  isPublic = false,
  onNewChat,
  onPlanModeClick,
  hasPlan = false,
  publicHeaderAuthAction,
  publicInlineSaveCta,
  sidecarCanvas,
  onRequestSidecarFocus,
  onProductCreationCommand,
  canFocusComposer = true,
  adjacentPaneContext = null
}) => {
  const { setIsNewChat } = useContext(ChatbotUIChatContext)

  const handleCreateChat = () => {
    setIsNewChat(true)
    setShowSidebar(false)

    if (onNewChat) {
      onNewChat()
      return
    }

    handleNewChat()
  }

  // `loadOpenThread` intentionally stays local to this screen so the route
  // effect only keys off chat identity changes and initial URL state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (chatId) {
      setIsNewChat(false)
    }
  }, [chatId])

  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { isSplitMode, navigateToHref } = useSplitPaneNavigation()
  const { theme } = useTheme()
  const addChatToAtom = useSetAtom(addChatAtom)

  const authState = useAuth()
  const { user } = authState
  const hasResolvedAuthSession = authState.session !== undefined
  const hasHandledTryQueryRef = useRef<string | false>(false)
  const hasHandledConnectorReturnRef = useRef('')
  const hasHandledDesktopHandoffRef = useRef('')
  const hasResetDesktopChatRef = useRef('')
  const hasHandledContentEngineHandoffRef = useRef('')
  const groupReadStateRequestKeyRef = useRef<string | null>(null)

  const {
    chats,
    chatActivityStatuses,
    getAttachmentStoreKey,
    getScopedAttachmentBindings,
    assistants,
    markChatActivitySeen,
    markSidecarChatSeen,
    setSelectedAssistant,
    setUseRetrieval,
    showSidebar,
    setShowSidebar,
    selectedAssistant,
    selectedWorkspace,
    setSelectedWorkspace,
    workspaces,
    refreshAccessibleWorkspaces,
    sidecarChatStatuses,
    allModels,
    profile,
    tools,
    setTools,
    setChats,
    setActiveAttachmentOverrideId
  } = useContext(ChatbotUIContext)

  // Hide sidebar for public chat
  useEffect(() => {
    if (isPublic) {
      setShowSidebar(false)
    }
  }, [isPublic, setShowSidebar])

  const {
    chatSettings,
    getChatStoreKey,
    getScopedChatBindings,
    setSelectedChat,
    selectedChat,
    setChatSettings,
    setSelectedTools,
    chatMessages: contextChatMessages,
    setChatMessages: setContextChatMessages,
    isGenerating: contextIsGenerating,
    isPersisting: contextIsPersisting,
    isSelecting,
    setActiveChatOverrideId,
    pendingNavigationChatId
  } = useContext(ChatbotUIChatContext)

  const scopedSurfaceChatBindings =
    chatId ? getScopedChatBindings(getChatStoreKey(chatId)) : null
  const scopedSurfaceChatState = scopedSurfaceChatBindings?.getSnapshot() ?? null
  const chatMessages = scopedSurfaceChatState?.chatMessages ?? contextChatMessages
  const sortedChatMessages = useMemo(
    () => sortChatMessagesByThreadOrder(chatMessages ?? []),
    [chatMessages]
  )
  const latestPersistedMessage = useMemo(
    () => (chatId ? getLatestPersistedChatMessage(sortedChatMessages, chatId) : null),
    [chatId, sortedChatMessages]
  )
  const persistedPendingAssistantTurn = useMemo(
    () => getLatestPendingAssistantTurnState(sortedChatMessages),
    [sortedChatMessages]
  )
  const hasPublicAssistantResponse = useMemo(
    () =>
      isPublic &&
      sortedChatMessages.some(
        (message) =>
          message.message.role === 'assistant' &&
          typeof message.message.content === 'string' &&
          message.message.content.trim().length > 0
      ),
    [isPublic, sortedChatMessages]
  )
  const setChatMessages =
    scopedSurfaceChatBindings?.setChatMessages ?? setContextChatMessages
  const isGenerating = Boolean(
    (scopedSurfaceChatState?.isGenerating ?? contextIsGenerating) ||
      persistedPendingAssistantTurn
  )
  const isPersisting = scopedSurfaceChatState?.isPersisting ?? contextIsPersisting
  const [chatHighlights, setChatHighlights] = useState<ChatHighlight[]>([])
  // Canvas browser is admin-released: platform admins always see it, everyone
  // else only once it is rolled out in admin settings.
  const { enabled: canvasBrowserBetaEnabled } = useBetaFeatureEnabled(
    CANVAS_BROWSER_BETA_FEATURE_KEY
  )
  // Pre-warm server-side caches on mount so tools-stream doesn't re-fetch
  // stable data (profile, soul stack, kids mode, workspace context) per message.
  usePrewarmChat({
    chatId: chatId ?? null,
    workspaceId: selectedWorkspace?.id ?? null,
    enabled: !isPublic && Boolean(user?.id)
  })

  const [persistedGeneratedImages, setPersistedGeneratedImages] = useState<ImageStackImage[]>([])
  const [selectedGeneratedImage, setSelectedGeneratedImage] =
    useState<ImageStackImage | null>(null)
  const [isDownloadingGeneratedImages, setIsDownloadingGeneratedImages] = useState(false)
  const createdArtifactMessages = useMemo(
    () =>
      sortedChatMessages.map(chatMessage => ({
        id: chatMessage.message.id,
        content: chatMessage.message.content,
        annotation: (chatMessage.message as { annotation?: unknown }).annotation,
        created_at: chatMessage.message.created_at,
        role: chatMessage.message.role,
        sequence_number: chatMessage.message.sequence_number
      })),
    [sortedChatMessages]
  )
  const loadedGeneratedImages = useMemo(
    () => extractGeneratedChatImages(createdArtifactMessages),
    [createdArtifactMessages]
  )
  const generatedImageStackImages = useMemo(
    () => mergeImageStackImages(persistedGeneratedImages, loadedGeneratedImages),
    [loadedGeneratedImages, persistedGeneratedImages]
  )
  const chatCreatedArtifacts = useMemo(() => {
    const fromMessages = extractChatCreatedArtifacts(createdArtifactMessages)
    const imageIds = new Set(
      fromMessages.filter(a => a.kind === 'image').map(a => a.id)
    )
    const persistedAsArtifacts: ChatCreatedArtifact[] = generatedImageStackImages
      .filter(image => !imageIds.has(`image:${image.url}`))
      .map(image => ({
        id: `image:${image.url}`,
        kind: 'image' as const,
        title: image.name || image.alt || 'Generated image',
        subtitle: 'Image',
        url: image.url,
        alt: image.alt,
        messageId: image.id
      }))
    return [...persistedAsArtifacts, ...fromMessages]
  }, [createdArtifactMessages, generatedImageStackImages])

  useEffect(() => {
    let cancelled = false

    if (!chatId || isPublic) {
      setChatHighlights([])
      return () => {
        cancelled = true
      }
    }

    const cachedHighlights = readCachedChatHighlights(chatId)
    setChatHighlights(cachedHighlights)

    fetchChatHighlights(chatId)
      .then((serverHighlights) => {
        if (cancelled) return

        const mergedHighlights = mergeHighlightsBySelection(
          serverHighlights,
          cachedHighlights
        )
        const unsyncedCachedHighlights = cachedHighlights.filter(
          (highlight) => !hasSameHighlightSelection(serverHighlights, highlight)
        )

        setChatHighlights(mergedHighlights)
        writeCachedChatHighlights(chatId, mergedHighlights)

        if (unsyncedCachedHighlights.length === 0) return

        void Promise.allSettled(
          unsyncedCachedHighlights.map((highlight) =>
            saveChatHighlight(chatId, {
              messageId: highlight.messageId,
              text: highlight.text,
              startOffset: highlight.startOffset,
              endOffset: highlight.endOffset,
              prefix: highlight.prefix,
              suffix: highlight.suffix
            })
          )
        ).then((results) => {
          if (cancelled) return

          const savedHighlights = results.flatMap((result) =>
            result.status === 'fulfilled' ? [result.value] : []
          )

          if (savedHighlights.length === 0) {
            safeConsole.warn('Failed to migrate cached chat highlights')
            return
          }

          setChatHighlights((currentHighlights) => {
            const withoutMigratedCache = currentHighlights.filter(
              (highlight) => !hasSameHighlightSelection(savedHighlights, highlight)
            )
            const nextHighlights = mergeHighlightsBySelection(
              savedHighlights,
              withoutMigratedCache
            )

            writeCachedChatHighlights(chatId, nextHighlights)

            return nextHighlights
          })
        })
      })
      .catch((error) => {
        if (cancelled) return

        safeConsole.warn('Failed to load chat highlights:', error)
      })

    return () => {
      cancelled = true
    }
  }, [chatId, isPublic])

  useEffect(() => {
    let cancelled = false

    if (!chatId || isPublic) {
      setPersistedGeneratedImages([])
      return () => {
        cancelled = true
      }
    }

    authenticatedFetch(`/api/chat/${encodeURIComponent(chatId)}/generated-images`, {
      cache: 'no-store',
      credentials: 'include'
    })
      .then(async response => {
        if (!response.ok) {
          throw new Error('Failed to load generated images')
        }

        return readGeneratedImagesResponse(response)
      })
      .then(images => {
        if (cancelled) return
        setPersistedGeneratedImages(images)
      })
      .catch(error => {
        if (cancelled) return
        safeConsole.error('Failed to load generated image stack:', error)
        setPersistedGeneratedImages([])
      })

    return () => {
      cancelled = true
    }
  }, [chatId, isPublic, latestPersistedMessage?.id])

  const handleCreateHighlight = useCallback(
    (draft: ChatHighlightDraft) => {
      if (!chatId || isPublic) return

      const text = draft.text.trim()
      if (!text) return

      const optimisticHighlight: ChatHighlight = {
        id: createHighlightId(),
        chatId,
        messageId: draft.messageId,
        text,
        startOffset: draft.startOffset,
        endOffset: draft.endOffset,
        prefix: draft.prefix,
        suffix: draft.suffix,
        createdAt: new Date().toISOString()
      }

      setChatHighlights((currentHighlights) => {
        const nextHighlights = hasSameHighlightSelection(
          currentHighlights,
          optimisticHighlight
        )
          ? currentHighlights
          : [optimisticHighlight, ...currentHighlights]

        writeCachedChatHighlights(chatId, nextHighlights)

        return nextHighlights
      })

      void saveChatHighlight(chatId, { ...draft, text })
        .then((savedHighlight) => {
          setChatHighlights((currentHighlights) => {
            const withoutOptimisticHighlight = currentHighlights.filter(
              (highlight) => highlight.id !== optimisticHighlight.id
            )
            const nextHighlights = hasSameHighlightSelection(
              withoutOptimisticHighlight,
              savedHighlight
            )
              ? withoutOptimisticHighlight
              : [savedHighlight, ...withoutOptimisticHighlight]

            writeCachedChatHighlights(chatId, nextHighlights)

            return nextHighlights
          })
        })
        .catch((error) => {
          safeConsole.warn('Failed to save chat highlight:', error)
          setChatHighlights((currentHighlights) => {
            const nextHighlights = currentHighlights.filter(
              (highlight) => highlight.id !== optimisticHighlight.id
            )

            writeCachedChatHighlights(chatId, nextHighlights)

            return nextHighlights
          })
          toast.error('Highlight could not be saved')
        })
    },
    [chatId, isPublic]
  )

  const handleDeleteHighlight = useCallback(
    (highlightId: string) => {
      if (!chatId || isPublic) return

      let removedHighlight: ChatHighlight | undefined

      setChatHighlights((currentHighlights) => {
        removedHighlight = currentHighlights.find(
          (highlight) => highlight.id === highlightId
        )
        const nextHighlights = currentHighlights.filter(
          (highlight) => highlight.id !== highlightId
        )

        writeCachedChatHighlights(chatId, nextHighlights)

        return nextHighlights
      })

      void deleteChatHighlight(chatId, highlightId).catch((error) => {
        safeConsole.warn('Failed to delete chat highlight:', error)

        if (!removedHighlight) return

        setChatHighlights((currentHighlights) => {
          if (currentHighlights.some((highlight) => highlight.id === highlightId)) {
            return currentHighlights
          }

          const nextHighlights = mergeHighlightsBySelection(
            [removedHighlight],
            currentHighlights
          )
          writeCachedChatHighlights(chatId, nextHighlights)

          return nextHighlights
        })
        toast.error('Highlight could not be removed')
      })
    },
    [chatId, isPublic]
  )

  useLayoutEffect(() => {
    // Keep the just-created chat selected while realtime (or any send)
    // navigates /chat → /chat/[id]. Clearing here raced a second create
    // when onConnected re-entered start with selectedChat=null.
    if (
      !chatId &&
      (pathname === '/chat' || forceDraftChat) &&
      selectedChat?.id &&
      selectedChat.id !== pendingNavigationChatId
    ) {
      setSelectedChat(null)
    }
  }, [
    chatId,
    forceDraftChat,
    pathname,
    pendingNavigationChatId,
    selectedChat?.id,
    setSelectedChat
  ])

  useLayoutEffect(() => {
    if (!isSplitMode && pathname?.startsWith('/chat')) {
      setActiveChatOverrideId(null)
      setActiveAttachmentOverrideId(null)
      return
    }

    setActiveChatOverrideId(chatId || null)
    setActiveAttachmentOverrideId(chatId || null)

    return () => {
      setActiveChatOverrideId((current) => (current === (chatId || null) ? null : current))
      setActiveAttachmentOverrideId((current) =>
        current === (chatId || null) ? null : current
      )
    }
  }, [
    chatId,
    isSplitMode,
    pathname,
    setActiveAttachmentOverrideId,
    setActiveChatOverrideId
  ])

  const surfaceSelectedChat = resolveSurfaceSelectedChat({
    chatId,
    pathname,
    selectedChat
  })

  useEffect(() => {
    if (!chatId) {
      return
    }

    const nextSelectedChat = chats.find((candidate) => candidate.id === chatId)

    if (!nextSelectedChat) {
      return
    }

    setSelectedChat((previousChat) => {
      if (
        previousChat?.id === nextSelectedChat.id &&
        JSON.stringify(previousChat) === JSON.stringify(nextSelectedChat)
      ) {
        return previousChat
      }

      return nextSelectedChat
    })
  }, [chatId, chats, setSelectedChat])

  const isMentionOnlyGroupChat =
    Boolean(surfaceSelectedChat?.is_group_chat) &&
    getGroupChatResponseMode(surfaceSelectedChat?.metadata) === 'mention_only'
  const canShareSurfaceChat = Boolean(surfaceSelectedChat && !surfaceSelectedChat.is_group_chat)

  const sidecarSystemEnabled = Boolean(sidecarEnabled && !isPublic)
  const sidecarChatId = chatId ?? surfaceSelectedChat?.id ?? null
  const sidecarStatusForActiveChat = sidecarChatId
    ? sidecarChatStatuses[sidecarChatId] ?? null
    : null
  const sidecarSeedRuns = useMemo(
    () => {
      const statusSeedRun = buildSeedRunFromSidecarStatus(sidecarStatusForActiveChat)

      return [
        ...(statusSeedRun ? [statusSeedRun] : []),
        ...(chatMessages ?? [])
          .map(buildSeedRunFromMessage)
          .filter((run): run is SidecarRunState => Boolean(run))
      ].sort((a, b) => b.startedAt - a.startedAt)
    },
    [chatMessages, sidecarStatusForActiveChat]
  )
  const {
    runs: sidecarRuns,
    selectRun: selectSidecarRun
  } = useSidecarRun({
    chatId: sidecarCanvas ? undefined : sidecarChatId,
    enabled: sidecarSystemEnabled && !sidecarCanvas,
    seedRuns: sidecarSeedRuns
  })
  const [sidecarPanelOpen, setSidecarPanelOpen] = useState(false)
  const [queuedSidecarTurns, setQueuedSidecarTurns] = useState<QueuedSidecarTurn[]>([])

  const effectiveRuns = sidecarCanvas?.runs ?? sidecarRuns
  const effectiveLatestRun = effectiveRuns[0] ?? null
  const effectiveSelectRun = sidecarCanvas?.selectRun ?? selectSidecarRun
  const effectiveSetPanelOpen = sidecarCanvas?.setPanelOpen ?? setSidecarPanelOpen
  const effectiveQueuedSidecarTurns = sidecarCanvas?.queuedTurnsArray ?? queuedSidecarTurns
  const effectiveSetQueuedSidecarTurns = sidecarCanvas?.setQueuedTurns ?? setQueuedSidecarTurns

  const queuedSidecarDispatchRef = useRef(false)
  const requestedQueuedTurnIdRef = useRef<string | null>(null)
  const lastSeenSidecarRunRef = useRef<string | null>(null)
  const sidecarRunActive = Boolean(
    sidecarSystemEnabled &&
      (effectiveLatestRun?.status === 'planning' ||
        effectiveLatestRun?.status === 'running')
  )

  useEffect(() => {
    const latestRunId = effectiveLatestRun?.runId ?? null
    if (!latestRunId) {
      lastSeenSidecarRunRef.current = null
      return
    }

    const isActiveRun =
      effectiveLatestRun?.status === 'planning' ||
      effectiveLatestRun?.status === 'running'

    // Only auto-open for newly seen *active* Sidecar runs. Do not open the
    // panel just because history hydration introduced a completed seed run,
    // or because the selected run id changed while idle.
    if (lastSeenSidecarRunRef.current === latestRunId) {
      return
    }

    lastSeenSidecarRunRef.current = latestRunId

    if (!isActiveRun) {
      return
    }

    effectiveSelectRun(latestRunId, { manual: false })
    effectiveSetPanelOpen(true)
  }, [
    effectiveLatestRun?.runId,
    effectiveLatestRun?.status,
    sidecarChatId,
    effectiveSelectRun,
    effectiveSetPanelOpen
  ])

  useEffect(() => {
    if (!sidecarSystemEnabled) return

    const handleOpenSidecarPanel = (event: Event) => {
      const detail = (event as CustomEvent<{ runId?: string }>).detail

      onRequestSidecarFocus?.()
      if (detail?.runId) {
        effectiveSelectRun(detail.runId)
      }
      effectiveSetPanelOpen(true)
      window.dispatchEvent(new CustomEvent('focusChatInput'))
    }

    window.addEventListener(
      'openSidecarPanel',
      handleOpenSidecarPanel as EventListener
    )

    return () => {
      window.removeEventListener(
        'openSidecarPanel',
        handleOpenSidecarPanel as EventListener
      )
    }
  }, [sidecarSystemEnabled, effectiveSelectRun, effectiveSetPanelOpen, onRequestSidecarFocus])

  const setUserInput = useScopedSetUserInput()
  const [desktopHandoffVersion, setDesktopHandoffVersion] = useState(0)
  const [contentEngineHandoffVersion, setContentEngineHandoffVersion] = useState(0)
  const composerLayoutGroupId = useId()
  const composerLayoutId = 'chat-composer'
  // Minimap: show on scroll, dismiss on click outside
  const [minimapVisible, setMinimapVisible] = useState(false)
  // Track previous scroll position to detect scroll direction
  const previousScrollTopRef = useRef<number>(0)

  // Ref to measure input container height for scroll button positioning
  const inputContainerRef = useRef<HTMLDivElement>(null)
  const responseControlsRef = useRef<HTMLDivElement>(null)
  const [inputContainerHeight, setInputContainerHeight] = useState(190)
  const [responseControlsHeight, setResponseControlsHeight] = useState(0)

  // Also update height dynamically during animations
  const updateInputHeight = useCallback(() => {
    const inputHeight = inputContainerRef.current?.offsetHeight ?? 0
    const controlsHeight = responseControlsRef.current?.offsetHeight ?? responseControlsHeight
    // Ensure minimum height so scroll button never overlaps the input
    setInputContainerHeight(Math.max(140, inputHeight + controlsHeight + 20))
  }, [responseControlsHeight])

  const updateResponseControlsHeight = useCallback(() => {
    setResponseControlsHeight(responseControlsRef.current?.offsetHeight ?? 0)
  }, [])

  // Use ResizeObserver to track dynamic height changes
  useEffect(() => {
    const observedElements = [
      inputContainerRef.current,
      responseControlsRef.current
    ].filter(Boolean) as HTMLDivElement[]

    if (observedElements.length === 0) return

    const observer = new ResizeObserver(() => {
      updateResponseControlsHeight()
      updateInputHeight()
    })
    observedElements.forEach((element) => observer.observe(element))

    return () => observer.disconnect()
  }, [updateInputHeight, updateResponseControlsHeight])

  // Initialize state early to avoid temporal dead zone issues
  const [loading, setLoading] = useState<boolean>(true)
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false)
  const [chatNotFound, setChatNotFound] = useState<boolean>(false)
  const [latestScrollRequest, setLatestScrollRequest] = useState<{
    chatId: string
    nonce: number
  } | null>(null)
  const chatLoadRequestRef = useRef(0)
  const hydratedChatIdsRef = useRef(new Set<string>())
  const pendingRealtimeRefreshRef = useRef(false)
  const pendingRealtimeRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const chatIdRef = useRef(chatId)
  const sessionExpiredRedirectRef = useRef(false)

  useEffect(() => {
    chatIdRef.current = chatId
  }, [chatId])
  const isQueuedReplayBlocked = useMemo(() => {
    if (loading || isTransitioning || sidecarRunActive) return true

    const liveSnapshot = scopedSurfaceChatBindings?.getSnapshot()
    if (liveSnapshot) {
      // Only a live (non-aborted) abortController means a real in-flight turn.
      // Ghost isGenerating/isPersisting or an already-aborted controller must
      // not block queue replay / force-send.
      return Boolean(
        liveSnapshot.abortController &&
          !liveSnapshot.abortController.signal.aborted
      )
    }

    return Boolean(isGenerating || isPersisting)
  }, [
    isGenerating,
    isPersisting,
    isTransitioning,
    loading,
    scopedSurfaceChatBindings,
    sidecarRunActive
  ])

  const { handleNewChat, handleFocusChatInput, handleSendMessage, handleStopMessage } =
    useChatHandler({
      onChatCreate,
      onChatUpdate,
      chatId,
      forceDraftChat,
      enableRealtimeFallback: true,
      canFocusInput: canFocusComposer,
      adjacentPaneContext
    })
  const handleSendMessageWithSidecarQueue = useCallback(
    async (
      message: string,
      currentMessages: ChatMessage[],
      isRegeneration: boolean,
      options?: ChatSendOptions
    ) => {
      // Crew / launcher "start a brand-new chat and send" must never land in
      // the Sidecar follow-up queue of a busy prior thread.
      if (options?.forceNewChat === true) {
        return handleSendMessage(message, currentMessages, isRegeneration, options)
      }

      // Only queue behind a LIVE stream (abortController) or an active Sidecar
      // run. Stale isGenerating/isPersisting without a controller is the
      // "press send twice" / force-send dead-end — clear the ghost and send.
      const liveBindings =
        chatId && getScopedChatBindings && getChatStoreKey
          ? getScopedChatBindings(getChatStoreKey(chatId))
          : null
      const liveSnapshot = liveBindings?.getSnapshot() ?? null
      const hasLiveStream = Boolean(
        liveSnapshot?.abortController &&
          !liveSnapshot.abortController.signal.aborted
      )
      const looksBusy =
        Boolean(isGenerating || isPersisting || liveSnapshot?.isGenerating || liveSnapshot?.isPersisting)

      if (!isRegeneration && looksBusy && !hasLiveStream && !sidecarRunActive && liveBindings) {
        // Clear ghost locks + any stale pending assistant above a newer turn.
        liveBindings.setChatMessages((previous) =>
          stopStalePendingAssistantTurns(previous)
        )
        liveBindings.setIsGenerating(false)
        liveBindings.setIsPersisting(false)
        liveBindings.setFirstTokenReceived(false)
        liveBindings.setAbortController(null)
      }

      const shouldQueueFollowUp =
        !isRegeneration && (sidecarRunActive || (looksBusy && hasLiveStream))

      if (!shouldQueueFollowUp) {
        return handleSendMessage(message, currentMessages, isRegeneration, options)
      }

      const trimmedMessage = message.trim()
      const attachmentBindings =
        getScopedAttachmentBindings && getAttachmentStoreKey
          ? getScopedAttachmentBindings(getAttachmentStoreKey(sidecarChatId))
          : null
      const queuedAttachments =
        options?.queuedAttachments ??
        (options?.hasAttachments && attachmentBindings
          ? attachmentBindings.getSnapshot()
          : null)
      const hasQueuedAttachments = hasQueuedDraftAttachments(queuedAttachments)

      if (!trimmedMessage && !hasQueuedAttachments) return

      options?.onAccepted?.()

      if (attachmentBindings && queuedAttachments && hasQueuedAttachments) {
        removeQueuedDraftAttachments(attachmentBindings, queuedAttachments)
      }

      const queueableOptions = getQueueableChatSendOptions({
        ...options,
        hasAttachments: Boolean(options?.hasAttachments || hasQueuedAttachments),
        ...(queuedAttachments && hasQueuedAttachments
          ? { queuedAttachments }
          : {})
      })

      effectiveSetQueuedSidecarTurns((previous) => [
        ...previous,
        {
          id: crypto.randomUUID(),
          message,
          options: queueableOptions,
          queuedAfterRunId: sidecarRunActive ? effectiveLatestRun?.runId ?? null : null
        }
      ])

      if (sidecarRunActive) {
        effectiveSetPanelOpen(true)
      }

      toast.info('Queued to send after the current response finishes.')
    },
    [
      chatId,
      effectiveLatestRun?.runId,
      effectiveSetPanelOpen,
      effectiveSetQueuedSidecarTurns,
      getAttachmentStoreKey,
      getChatStoreKey,
      getScopedAttachmentBindings,
      getScopedChatBindings,
      handleSendMessage,
      isGenerating,
      isPersisting,
      sidecarChatId,
      sidecarRunActive
    ]
  )
  const handleNewChatRef = useRef(handleNewChat)
  const handleSendMessageRef = useRef(handleSendMessageWithSidecarQueue)
  const latestChatMessagesRef = useRef(chatMessages)

  useEffect(() => {
    handleNewChatRef.current = handleNewChat
  }, [handleNewChat])

  useEffect(() => {
    handleSendMessageRef.current = handleSendMessageWithSidecarQueue
  }, [handleSendMessageWithSidecarQueue])

  useEffect(() => {
    latestChatMessagesRef.current = chatMessages
  }, [chatMessages])

  useEffect(() => {
    effectiveSetQueuedSidecarTurns([])
    queuedSidecarDispatchRef.current = false
    requestedQueuedTurnIdRef.current = null
  }, [sidecarChatId, effectiveSetQueuedSidecarTurns])

  const buildQueuedTurnOptions = useCallback(
    (turn: QueuedSidecarTurn): ChatSendOptions | undefined => {
      // Queueing behind a busy Sidecar/stream must NOT force the follow-up
      // into Sidecar. Only preserve an explicit sidecar option the sender set.
      const queuedReplayOptions: ChatSendOptions = {
        ...turn.options,
        forceAssistantResponse: true
      }

      if (turn.options?.sidecar?.mode === 'force') {
        return {
          ...queuedReplayOptions,
          sidecar: {
            ...turn.options.sidecar,
            reason: turn.options.sidecar.reason ?? 'queued_follow_up',
            queuedAfterRunId:
              turn.queuedAfterRunId ??
              turn.options.sidecar.queuedAfterRunId ??
              effectiveLatestRun?.runId ??
              null
          }
        }
      }

      return queuedReplayOptions
    },
    [effectiveLatestRun?.runId]
  )

  const sendQueuedTurnBatch = useCallback(
    async (batch: QueuedSidecarTurnReplayBatch) => {
      let sendAccepted = false
      const queuedOptions = batch.options
      const queuedTurnIds = new Set(batch.turns.map((turn) => turn.id))

      await Promise.resolve(
        handleSendMessage(batch.message, latestChatMessagesRef.current, false, {
          ...queuedOptions,
          onAccepted: () => {
            sendAccepted = true
            if (
              requestedQueuedTurnIdRef.current &&
              queuedTurnIds.has(requestedQueuedTurnIdRef.current)
            ) {
              requestedQueuedTurnIdRef.current = null
            }
            effectiveSetQueuedSidecarTurns((previous) =>
              previous.filter((candidate) => !queuedTurnIds.has(candidate.id))
            )
            queuedOptions?.onAccepted?.()
          },
          onRejected: () => {
            if (sendAccepted) {
              effectiveSetQueuedSidecarTurns((previous) => {
                const missingTurns = batch.turns.filter(
                  (turn) => !previous.some((candidate) => candidate.id === turn.id)
                )

                return missingTurns.length > 0
                  ? [...missingTurns, ...previous]
                  : previous
              })
            }
            queuedOptions?.onRejected?.()
          },
          onResponsePersisted: () => {
            queuedOptions?.onResponsePersisted?.()
          }
        })
      )

      return sendAccepted
    },
    [effectiveSetQueuedSidecarTurns, handleSendMessage]
  )

  const handleQueuedTurnSendError = useCallback(
    (error: unknown) => {
      captureSharedChatFollowUpException(error, {
        source: 'chat-ui-queued-follow-up',
        stage: 'follow_up_send',
        severity: 'error',
        path: pathname
      })
      toast.error(
        error instanceof Error
          ? error.message
          : "Couldn't send the queued follow-up. Try again from the composer."
      )
    },
    [pathname]
  )

  const sendQueuedTurnNow = useCallback(
    (id: string) => {
      const hasQueuedTurn = effectiveQueuedSidecarTurns.some(
        (candidate) => candidate.id === id
      )

      if (!hasQueuedTurn) return

      requestedQueuedTurnIdRef.current = id

      // Stop any live turn first so the force-send is not blocked by a pending
      // assistant / generating lock. Never restore the stopped draft into the
      // composer — the queued turn is what the user wants to send.
      const liveBindings =
        chatId && getScopedChatBindings && getChatStoreKey
          ? getScopedChatBindings(getChatStoreKey(chatId))
          : null
      const liveSnapshot = liveBindings?.getSnapshot()
      if (
        isGenerating ||
        liveSnapshot?.isGenerating ||
        (liveSnapshot?.abortController &&
          !liveSnapshot.abortController.signal.aborted)
      ) {
        handleStopMessage({ restoreInput: false })
      }

      // Clear any ghost draft left from the aborted turn before replay.
      setUserInput('')

      if (loading || isTransitioning) {
        toast.info('Queued follow-up will send when the current response is ready.')
        return
      }

      if (queuedSidecarDispatchRef.current) return

      // Snapshot before optimistic queue removal — the deferred send still
      // needs these turns. Removing the whole batch now prevents a second
      // force-send / auto-replay from double-firing (queue corruption).
      // onRejected re-adds missing turns if the send fails before accept.
      const turnsSnapshot = effectiveQueuedSidecarTurns
      const requestedId = requestedQueuedTurnIdRef.current
      const batchPreview = getQueuedSidecarTurnReplayBatch(
        turnsSnapshot,
        requestedId,
        buildQueuedTurnOptions
      )
      if (!batchPreview) {
        return
      }
      const optimisticIds = new Set(batchPreview.turns.map((turn) => turn.id))
      effectiveSetQueuedSidecarTurns((previous) =>
        previous.filter((candidate) => !optimisticIds.has(candidate.id))
      )

      // Also clear stale pending assistants so the deferred send is not
      // soft-blocked by streamStatus:'running' above a newer user turn.
      if (liveBindings) {
        liveBindings.setChatMessages((previous) =>
          stopStalePendingAssistantTurns(previous)
        )
      }

      // Defer one macrotask so stop's sync store writes are visible to
      // handleSendMessage, and so we do not race React's still-stale
      // isPersisting/isGenerating props from this render.
      queuedSidecarDispatchRef.current = true
      window.setTimeout(() => {
        void sendQueuedTurnBatch(batchPreview)
          .catch(handleQueuedTurnSendError)
          .finally(() => {
            queuedSidecarDispatchRef.current = false
          })
      }, 0)
    },
    [
      buildQueuedTurnOptions,
      chatId,
      effectiveQueuedSidecarTurns,
      effectiveSetQueuedSidecarTurns,
      getChatStoreKey,
      getScopedChatBindings,
      handleQueuedTurnSendError,
      handleStopMessage,
      isGenerating,
      isTransitioning,
      loading,
      sendQueuedTurnBatch,
      setUserInput
    ]
  )

  useEffect(() => {
    if (effectiveQueuedSidecarTurns.length === 0) return
    if (queuedSidecarDispatchRef.current) return
    if (isQueuedReplayBlocked) return

    const batch = getQueuedSidecarTurnReplayBatch(
      effectiveQueuedSidecarTurns,
      requestedQueuedTurnIdRef.current,
      buildQueuedTurnOptions
    )

    if (!batch) return

    queuedSidecarDispatchRef.current = true

    void sendQueuedTurnBatch(batch)
      .catch(handleQueuedTurnSendError)
      .finally(() => {
        queuedSidecarDispatchRef.current = false
      })
  }, [
    buildQueuedTurnOptions,
    effectiveQueuedSidecarTurns,
    handleQueuedTurnSendError,
    isQueuedReplayBlocked,
    sendQueuedTurnBatch
  ])

  const { handleSelectPromptWithVariables, handleSelectAssistant } = usePromptAndCommand({
    chatId
  })
  const {
    scrollRef,
    messagesStartRef,
    messagesEndRef,
    handleScroll: originalHandleScroll,
    scrollToBottom: originalScrollToBottom,
    pinElementToTop,
    setIsAtBottom,
    setUserScrolled,
    isAtTop,
    isAtBottom,
    isOverflowing,
    scrollToTop,
    getDistanceFromBottom
  } = useScroll({
    scrollToMessageId: searchParams?.get('highlight') ?? undefined,
    pinToTop: !isPublic,
    // Sticky composer overlays the scroller bottom — pin-aware "bottom" must
    // clear this inset or scroll-to-bottom still looks like empty void.
    composerInset: inputContainerHeight
  })
  const highlightedMessageId = searchParams?.get('highlight')

  useEffect(() => {
    const handleLatestScrollRequest = (event: Event) => {
      const requestedChatId = (
        event as CustomEvent<{ chatId?: string }>
      ).detail?.chatId

      if (!requestedChatId) return

      setLatestScrollRequest({
        chatId: requestedChatId,
        nonce: Date.now()
      })
    }

    window.addEventListener(
      CHAT_LATEST_SCROLL_REQUEST_EVENT,
      handleLatestScrollRequest as EventListener
    )

    return () => {
      window.removeEventListener(
        CHAT_LATEST_SCROLL_REQUEST_EVENT,
        handleLatestScrollRequest as EventListener
      )
    }
  }, [])

  useEffect(() => {
    if (!chatId || highlightedMessageId) return

    consumeChatLatestScrollRequest(chatId)
    setUserScrolled(false)
    setLatestScrollRequest({
      chatId,
      nonce: Date.now()
    })
  }, [chatId, highlightedMessageId, setUserScrolled])

  // Listen for soft-deleted messages and remove them from visible state
  useEffect(() => {
    const handleMessageDeleted = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        messageId: string
        chatId: string
      }
      if (detail.chatId !== chatId) return
      setChatMessages((prev) =>
        prev.filter((msg) => msg.message.id !== detail.messageId)
      )
    }

    window.addEventListener('losi:message-deleted', handleMessageDeleted)
    return () => {
      window.removeEventListener('losi:message-deleted', handleMessageDeleted)
    }
  }, [chatId, setChatMessages])

  // Authenticated launchers can start a new chat and send the query.
  useEffect(() => {
    if (isPublic) return
    if (pathname !== '/chat') return
    const crewLaunchNonce = searchParams?.get(CREW_CHAT_LAUNCH_PARAM)
    const query = searchParams?.get('query')
    const shouldAutoSendQuery =
      searchParams?.get('fromTry') === '1' ||
      searchParams?.get('fromSetup') === '1' ||
      searchParams?.get('fromSidecar') === '1'
    if (!crewLaunchNonce && !query) return
    if (!profile || !selectedWorkspace) return
    // Track the specific launch token so a second Crew send (same /chat
    // surface) re-fires instead of being blocked by a one-shot boolean.
    const launchToken = crewLaunchNonce ? `launch:${crewLaunchNonce}` : `query:${query}`
    if (hasHandledTryQueryRef.current === launchToken) return
    hasHandledTryQueryRef.current = launchToken

    // Your crew → chat: one-time same-origin launch (auto-send, bound to
    // the Crew member so tools use that member's browser / vault / rules).
    if (crewLaunchNonce) {
      const launch = consumeCrewChatLaunch(crewLaunchNonce)
      const run = async () => {
        try {
          if (launch) {
            // Reset draft UI first. Send with forceNewChat so a stale
            // selectedChat closure from a prior Crew thread cannot swallow
            // the message (or Sidecar-queue it) while the screen shows empty.
            await handleNewChatRef.current()
            bindCrewChatToNextChat({
              crewMemberId: launch.crewMemberId,
              roster: launch.roster
            })
            // Legacy single-member bind kept for older helpers that only read member.
            bindCrewMemberToNextChat(launch.crewMemberId)
            await handleSendMessageRef.current(launch.prompt, [], false, {
              forceNewChat: true
            })
          }
        } finally {
          router.replace('/chat')
        }
      }
      void run()
      return
    }

    if (!query) return

    // Plain `/chat?query=` links (Crew cards, Discuss, Go): prefill only.
    // Never auto-send from an arbitrary URL — a crafted link must not run
    // tools on the user's behalf.
    if (!shouldAutoSendQuery) {
      setUserInput(query)
      router.replace('/chat')
      return
    }

    const run = async () => {
      try {
        await handleNewChatRef.current()
        setChatSettings((prev) =>
          prev ? { ...prev, model: 'losi-omega' as const } : prev
        )
        await handleSendMessageRef.current(
          decodeURIComponent(query),
          [],
          false,
          { forceNewChat: true }
        )
      } finally {
        router.replace('/chat')
      }
    }
    void run()
  }, [
    isPublic,
    pathname,
    searchParams,
    profile,
    selectedWorkspace,
    setChatSettings,
    setUserInput,
    router
  ])

  // Resume chat after GitHub App / CLI connector OAuth return-to-page.
  // The install callback redirects to /chat/:id?github_app=installed (or
  // ?cli_connected=kiro_cli). Emit the same connector-connected event the
  // inline card uses so Losi continues with knowledge of the connection.
  useEffect(() => {
    if (isPublic) return

    const githubInstalled = searchParams?.get('github_app') === 'installed'
    const cliConnected = searchParams?.get('cli_connected')
    const connectorParam = searchParams?.get('connector_connected')

    let connector: LosiConnectorType | null = null
    if (githubInstalled) connector = 'github_app'
    else if (
      cliConnected === 'kiro_cli' ||
      cliConnected === 'codex_cli' ||
      cliConnected === 'claude_code'
    ) {
      connector = cliConnected
    } else if (
      connectorParam === 'github_app' ||
      connectorParam === 'kiro_cli' ||
      connectorParam === 'codex_cli' ||
      connectorParam === 'claude_code' ||
      connectorParam === 'repo_link'
    ) {
      connector = connectorParam
    }

    if (!connector) return

    const activeChatId = chatId || selectedChat?.id || ''
    if (!activeChatId) return

    const dedupeKey = `${activeChatId}:${connector}`
    if (hasHandledConnectorReturnRef.current === dedupeKey) return
    hasHandledConnectorReturnRef.current = dedupeKey

    const detail: ConnectorConnectedEventDetail = {
      chatId: activeChatId,
      connector,
      status: 'connected'
    }

    // Defer so useChatHandler's listener is mounted.
    setTimeout(() => {
      emitConnectorConnected(detail)
    }, 400)

    const url = new URL(window.location.href)
    url.searchParams.delete('github_app')
    url.searchParams.delete('cli_connected')
    url.searchParams.delete('connector_connected')
    router.replace(`${url.pathname}${url.search}${url.hash}`)
  }, [chatId, isPublic, router, searchParams, selectedChat?.id])

  useEffect(() => {
    if (!isDesktopHost()) return
    return onPendingDesktopHandoff(() => {
      setDesktopHandoffVersion((value) => value + 1)
    })
  }, [])

  useEffect(() => {
    return onPendingContentEngineChatHandoff(() => {
      setContentEngineHandoffVersion((value) => value + 1)
    })
  }, [])

  useEffect(() => {
    if (isPublic) return
    if (!pathname.startsWith('/chat')) return
    if (isGenerating) return
    if (!profile || !selectedWorkspace) return
    if (!chatSettings) return

    const pending = readPendingContentEngineChatHandoff()
    if (!pending) return

    const handoffKey = `${pending.id}:${pending.receivedAt}`
    if (hasHandledContentEngineHandoffRef.current === handoffKey) {
      return
    }

    if (pending.workspaceId && selectedWorkspace.id !== pending.workspaceId) {
      const targetWorkspace = workspaces.find((workspace) => workspace.id === pending.workspaceId)
      if (targetWorkspace) {
        setSelectedWorkspace(targetWorkspace)
      }
      return
    }

    hasHandledContentEngineHandoffRef.current = handoffKey

    const run = async () => {
      try {
        if (!pending.autoSend) {
          setUserInput(pending.message)
          consumePendingContentEngineChatHandoff(pending.id)
          return
        }

        await handleSendMessageRef.current(
          pending.message,
          pending.createChat ? [] : latestChatMessagesRef.current,
          false,
          {
            forceNewChat: pending.createChat
          }
        )
        consumePendingContentEngineChatHandoff(pending.id)
      } catch (error) {
        safeConsole.error('Failed to send Content Engine handoff', error)
        setUserInput(pending.message)
        consumePendingContentEngineChatHandoff(pending.id)
        toast.error('Content Engine handoff was added to the composer instead')
      } finally {
        hasHandledContentEngineHandoffRef.current = ''
      }
    }

    run()
  }, [
    chatSettings,
    contentEngineHandoffVersion,
    isGenerating,
    isPublic,
    pathname,
    profile,
    selectedWorkspace,
    setSelectedWorkspace,
    setUserInput,
    workspaces
  ])

  useEffect(() => {
    if (isPublic) return
    if (!pathname.startsWith('/chat')) return
    if (!isDesktopHost()) return
    if (isGenerating) return
    if (hasHandledDesktopHandoffRef.current) return
    if (readPendingDesktopHandoff()) return

    const syncDesktopIdleState = async () => {
      try {
        await Promise.resolve(window.electronAPI?.markLosiRendererReady?.())
      } catch (error) {
        safeConsole.warn('Failed to resync desktop renderer-ready state', error)
      }

      try {
        await Promise.resolve(window.electronAPI?.setLosiBusyState?.(false))
      } catch (error) {
        safeConsole.warn('Failed to clear stale desktop busy state', error)
      }
    }

    syncDesktopIdleState().catch(() => {})
  }, [desktopHandoffVersion, isGenerating, isPublic, pathname])

  useEffect(() => {
    if (isPublic) return
    if (!pathname.startsWith('/chat')) return
    if (!isDesktopHost()) return
    if (isGenerating) return
    if (!profile || !selectedWorkspace) return
    if (!chatSettings) return
    if (!allModels.length) return

    const pending = readPendingDesktopHandoff()
    if (!pending) return

    const activeModelId = pending.model || chatSettings.model
    const hasActiveModel = allModels.some(
      (model) => model.modelId === activeModelId || model.hostedId === activeModelId
    )
    if (!hasActiveModel) {
      return
    }

    if (pending.model && chatSettings.model !== pending.model) {
      setChatSettings((prev) => ({
        ...(prev || {}),
        model: pending.model as LLMID,
        prompt: prev?.prompt || 'You are a helpful AI assistant.',
        temperature: prev?.temperature ?? 0.5,
        contextLength: prev?.contextLength ?? 4000,
        includeProfileContext: prev?.includeProfileContext ?? true,
        includeWorkspaceInstructions: prev?.includeWorkspaceInstructions ?? true,
        embeddingsProvider: prev?.embeddingsProvider ?? 'openai',
        reflectMode: prev?.reflectMode ?? false
      }))
      return
    }

    const handoffKey =
      pending.handoffId ||
      `${pending.message || ''}:${pending.receivedAt || 0}:${pending.createChat ? 'new' : 'same'}`

    if (!handoffKey) {
      return
    }

    const releaseDesktopHandoffBusyState = async () => {
      if (!window.electronAPI?.setLosiBusyState) {
        return
      }

      try {
        await Promise.resolve(window.electronAPI.setLosiBusyState(false))
      } catch (error) {
        safeConsole.warn('Failed to release desktop handoff busy state', error)
      }
    }

    if (pending.workspaceId && selectedWorkspace.id !== pending.workspaceId) {
      const targetWorkspace = workspaces.find((workspace) => workspace.id === pending.workspaceId)
      if (targetWorkspace) {
        setSelectedWorkspace(targetWorkspace)
      }
      return
    }

    if (pending.chatId) {
      const targetPath = `/chat/${pending.chatId}`
      if (pathname !== targetPath) {
        router.push(targetPath)
        return
      }

      if (selectedChat?.id !== pending.chatId) {
        return
      }
    }

    const hasPendingComposerContent =
      Boolean(pending.message) || pending.attachments.length > 0
    const shouldStartFreshChat =
      pending.createChat || (hasPendingComposerContent && !pending.chatId)

    if (shouldStartFreshChat && pathname !== '/chat') {
      if (hasResetDesktopChatRef.current === handoffKey) {
        return
      }

      hasResetDesktopChatRef.current = handoffKey
      handleNewChatRef.current('/chat').catch(async (error) => {
        consumePendingDesktopHandoff(pending.handoffId)
        hasResetDesktopChatRef.current = ''
        await reportPendingDesktopHandoffResult(
          pending.handoffId,
          false,
          error instanceof Error ? error.message : 'Desktop handoff failed.'
        )
        await releaseDesktopHandoffBusyState()
      })
      return
    }

    if (hasHandledDesktopHandoffRef.current === handoffKey) {
      return
    }

    hasHandledDesktopHandoffRef.current = handoffKey

    const run = async () => {
      let handoffAcknowledged = false
      let handoffConsumed = false

      const consumeHandoff = () => {
        if (handoffConsumed) {
          return
        }

        consumePendingDesktopHandoff(pending.handoffId)
        handoffConsumed = true
      }

      const acknowledgeHandoff = async () => {
        if (handoffAcknowledged) {
          return
        }

        consumeHandoff()
        hasResetDesktopChatRef.current = ''
        handoffAcknowledged = true
        await reportPendingDesktopHandoffResult(pending.handoffId, true)
      }

      const failHandoff = async (error: unknown) => {
        const errorMessage =
          error instanceof Error ? error.message : 'Desktop handoff failed.'

        if (handoffAcknowledged) {
          safeConsole.error('Desktop handoff failed after acknowledgement', error)
          return
        }

        consumeHandoff()
        hasResetDesktopChatRef.current = ''
        await reportPendingDesktopHandoffResult(pending.handoffId, false, errorMessage)
      }

      try {
        const imageAttachments = pending.attachments.filter(
          (attachment) => attachment.kind === 'image' || attachment.mimeType.startsWith('image/')
        )

        if (!pending.autoSend && imageAttachments.length > 0) {
          const handoffAttachmentKey = shouldStartFreshChat
            ? getAttachmentStoreKey(null)
            : getAttachmentStoreKey(pending.chatId || selectedChat?.id || null)
          const attachmentBindings = getScopedAttachmentBindings(
            handoffAttachmentKey
          )

          attachmentBindings.setShowFilesDisplay(true)
          attachmentBindings.setNewMessageImages((previous) => {
            const nextImages = [...previous]

            for (const attachment of imageAttachments) {
              const dedupeKey = attachment.path || attachment.signedUrl || attachment.id
              const exists = nextImages.some((image) => {
                const imageKey = image.path || image.url || image.messageId
                return imageKey === dedupeKey
              })

              if (exists) continue

              nextImages.push({
                messageId: attachment.id || crypto.randomUUID(),
                path: attachment.path,
                base64: attachment.signedUrl,
                url: attachment.signedUrl,
                file: null
              })
            }

            return nextImages
          })
        }

        let sendOperation: unknown = undefined

        if (!pending.autoSend) {
          setUserInput(pending.message)
          await acknowledgeHandoff()
        } else if (pending.message || pending.attachments.length > 0) {
          const handoffMessage =
            pending.message || 'Please analyze the attached content.'
          let accepted = false
          let acceptedAckPromise: Promise<void> | null = null
          const acknowledgeOnAccepted = () => {
            accepted = true
            acceptedAckPromise = acknowledgeHandoff()
          }

          sendOperation = handleSendMessageRef.current(
            handoffMessage,
            shouldStartFreshChat ? [] : latestChatMessagesRef.current,
            false,
            {
              forceNewChat: shouldStartFreshChat,
              hasAttachments: pending.attachments.length > 0,
              desktopHandoffAttachments: pending.attachments,
              onAccepted: acknowledgeOnAccepted
            }
          )

          await Promise.resolve(sendOperation)
          if (acceptedAckPromise) {
            await acceptedAckPromise
          } else if (!accepted) {
            await acknowledgeHandoff()
          }
        } else {
          await acknowledgeHandoff()
        }
      } catch (error) {
        await failHandoff(error)
      } finally {
        await releaseDesktopHandoffBusyState()
        hasResetDesktopChatRef.current = ''
        hasHandledDesktopHandoffRef.current = ''
      }
    }

    run()
  }, [
    desktopHandoffVersion,
    isGenerating,
    isPublic,
    pathname,
    profile,
    router,
    selectedChat,
    chatSettings,
    selectedWorkspace,
    allModels,
    getAttachmentStoreKey,
    getScopedAttachmentBindings,
    setChatSettings,
    setSelectedWorkspace,
    setUserInput,
    workspaces
  ])

  // Wrapper for scrollToBottom that resets suggestion state and updates distance.
  // Accept unknown so a mistaken onClick={scrollToBottom} (event as arg) cannot
  // leak into Element.scrollTo — resolve to a real ScrollBehavior first.
  const scrollToBottom = useCallback(
    (behavior?: unknown) => {
      const resolved: ScrollBehavior | undefined =
        behavior === 'auto' || behavior === 'smooth' || behavior === 'instant'
          ? behavior
          : undefined
      originalScrollToBottom(resolved)
      // Clear overlay immediately using pin-aware bottom (not raw scrollHeight /
      // spacer void). Otherwise the button stays up after click and feels broken.
      setCurrentDistanceFromBottom(0)

      // Reconcile distance after smooth scroll settles (instant 'auto' is ready now)
      const settleMs = resolved === 'auto' ? 32 : 500
      setTimeout(() => {
        if (scrollRef.current) {
          setCurrentDistanceFromBottom(getDistanceFromBottom())

          // Trigger scroll event so handler updates state naturally
          scrollRef.current.dispatchEvent(new Event('scroll', { bubbles: true }))
        }
      }, settleMs)
    },
    [getDistanceFromBottom, originalScrollToBottom, scrollRef]
  )

  // Track current scroll distance for the scroll-to-bottom control.
  const [currentDistanceFromBottom, setCurrentDistanceFromBottom] = useState(0)
  const shouldShowScrollToBottomButton =
    ((chatMessages && chatMessages.length > 0) || isGenerating) &&
    (currentDistanceFromBottom > 80 || (!isAtBottom && isOverflowing))

  // Suggestion links + questionnaires render inline under the last assistant
  // bubble (Message), not above the composer. Keep composer height in sync
  // with the input only.
  useEffect(() => {
    updateResponseControlsHeight()
    updateInputHeight()
  }, [isGenerating, updateInputHeight, updateResponseControlsHeight])

  // Let useScroll handle isAtBottom; here we track distance + minimap only.
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      originalHandleScroll(e)

      const target = e.currentTarget
      const currentScrollTop = target.scrollTop
      const distanceFromBottom = getDistanceFromBottom()
      setCurrentDistanceFromBottom(distanceFromBottom)

      const scrollDelta = currentScrollTop - previousScrollTopRef.current
      previousScrollTopRef.current = currentScrollTop

      if (Math.abs(scrollDelta) < 5) {
        return
      }

      setMinimapVisible(true)
    },
    [getDistanceFromBottom, originalHandleScroll]
  )

  // Initialize scroll position tracking
  useEffect(() => {
    if (scrollRef.current) {
      previousScrollTopRef.current = scrollRef.current.scrollTop
    }
  }, [scrollRef])

  // Discovery CTA: enable Create Images from feature discovery prompts.
  useEffect(() => {
    if (isPublic) return

    const enableImageGen = () => {
      const imageGenTool = tools.find((tool) => tool.name === 'Create Images')
      if (!imageGenTool) return
      setSelectedTools((prev) => {
        if (prev.some((tool) => tool.id === imageGenTool.id)) return prev
        return [...prev, imageGenTool]
      })
    }

    const drainPending = () => {
      void import('@/lib/discoverability/cta-actions').then(({ consumePendingEnableImageGen }) => {
        if (consumePendingEnableImageGen()) enableImageGen()
      })
    }

    drainPending()
    window.addEventListener('losi:enable-image-gen', enableImageGen)
    return () => {
      window.removeEventListener('losi:enable-image-gen', enableImageGen)
    }
  }, [isPublic, setSelectedTools, tools])

  // Listen for tool.enabled events from Losi
  const { addEventListener, removeEventListener } = useServerEvents()
  useEffect(() => {
    const handleToolEnabled = async (event: any) => {
      const payload = event?.data || event?.detail || {}
      const { toolId, toolName } = payload
      if (!toolId) return

      let toolToEnable = tools.find((tool) => tool.id === toolId)

      if (!toolToEnable) {
        try {
          const fetchedTool = await getToolById(toolId)
          toolToEnable = fetchedTool
          setTools((currentTools) =>
            currentTools.some((tool) => tool.id === toolId)
              ? currentTools
              : [fetchedTool, ...currentTools]
          )
        } catch {
          safeConsole.warn(`Tool with ID ${toolId} (${toolName}) not found in available tools`)
          return
        }
      }

      const enabledTool = toolToEnable

      // Add tool to selectedTools if not already selected
      setSelectedTools((prev) => {
        if (prev.some((t) => t.id === toolId)) {
          return prev // Already enabled
        }
        return [...prev, enabledTool]
      })

      safeConsole.log(`Tool "${toolName}" enabled by Losi`)
    }

    const handleToolDisabled = (event: any) => {
      const { toolId, toolName } = event.data || {}
      const normalizedToolName =
        typeof toolName === 'string' ? toolName.toLowerCase() : null

      if (!toolId && !normalizedToolName) return

      setSelectedTools((prev) =>
        prev.filter((tool) => {
          if (toolId && tool.id === toolId) {
            return false
          }

          return !normalizedToolName || tool.name.toLowerCase() !== normalizedToolName
        })
      )

      safeConsole.log(`Tool "${toolName ?? toolId}" disabled by Losi`)
    }

    const handleWindowToolEnabled = (event: Event) => {
      void handleToolEnabled(event as CustomEvent)
    }

    addEventListener('tool.enabled', handleToolEnabled)
    addEventListener('tool.disabled', handleToolDisabled)
    if (typeof window !== 'undefined') {
      window.addEventListener('losi:tool.enabled', handleWindowToolEnabled)
    }
    return () => {
      removeEventListener('tool.enabled', handleToolEnabled)
      removeEventListener('tool.disabled', handleToolDisabled)
      if (typeof window !== 'undefined') {
        window.removeEventListener('losi:tool.enabled', handleWindowToolEnabled)
      }
    }
  }, [addEventListener, removeEventListener, setTools, tools, setSelectedTools])

  const currentChatSnapshot = chatId
    ? getScopedChatBindings(getChatStoreKey(chatId)).getSnapshot()
    : null
  const visibleIsGenerating = Boolean(
    (currentChatSnapshot?.isGenerating ?? isGenerating) ||
      persistedPendingAssistantTurn
  )
  const visibleIsPersisting = currentChatSnapshot?.isPersisting ?? isPersisting
  const hasScopedChatData = Boolean(
    currentChatSnapshot &&
      (currentChatSnapshot.chatMessages.length > 0 ||
        currentChatSnapshot.chatFileItems.length > 0)
  )
  const hasHydratedCurrentChat = chatId
    ? hydratedChatIdsRef.current.has(chatId) ||
      hasScopedChatData
    : true
  const hasVerifiedCurrentChatAccess = chatId
    ? hydratedChatIdsRef.current.has(chatId) && !chatNotFound
    : true
  const chatRealtimeEnabled = Boolean(
    chatId &&
      user?.id &&
      hasResolvedAuthSession &&
      !isPublic &&
      hasVerifiedCurrentChatAccess
  )
  const shouldShowLoading =
    loading ||
    Boolean(
      chatId &&
        !chatNotFound &&
        !hasHydratedCurrentChat
    )
  const activeChatActivityStatus = chatId
    ? chatActivityStatuses[chatId] ?? null
    : null
  const activeSidecarStatus = chatId ? sidecarStatusForActiveChat : null
  const shouldMarkOpenChatSeen =
    Boolean(chatId) &&
    Boolean(chatMessages?.length) &&
    !shouldShowLoading &&
    (isAtBottom ||
      (!highlightedMessageId && latestScrollRequest?.chatId === chatId))

  const hydrateRealtimeMessageAssets = useCallback(
    async (message: Tables<'messages'>) => {
      const normalizedChatId = chatId?.trim()

      if (!normalizedChatId || message.chat_id !== normalizedChatId) {
        return
      }

      const [messageFileItemsRecord, images] = await Promise.all([
        getMessageFileItemsByMessageId(message.id).catch((error) => {
          safeConsole.warn('Failed to hydrate realtime message file items', error)
          return null
        }),
        fetchMessageImages([message]).catch((error) => {
          safeConsole.warn('Failed to hydrate realtime message images', error)
          return []
        })
      ])

      if (chatId?.trim() !== normalizedChatId) {
        return
      }

      const chatBindings = getScopedChatBindings(getChatStoreKey(normalizedChatId))
      const attachmentBindings = getScopedAttachmentBindings(
        getAttachmentStoreKey(normalizedChatId)
      )
      const realtimeFileItems = messageFileItemsRecord?.file_items ?? []

      if (realtimeFileItems.length > 0) {
        chatBindings.setChatFileItems((previousFileItems) =>
          mergeRealtimeFileItems(previousFileItems, realtimeFileItems)
        )
      }

      if (realtimeFileItems.length > 0) {
        chatBindings.setChatMessages((previousMessages) =>
          upsertParsedRealtimeChatMessage(previousMessages, {
            ...parseDBMessageCodeBlocksAndContent({
              ...message,
              metadata: message.metadata ?? null,
              type: message.type ?? null,
              file_items: realtimeFileItems.map((fileItem) => ({
                id: fileItem.id
              }))
            }),
            fileItems: realtimeFileItems.map((fileItem) => fileItem.id)
          })
        )
      }

      if (realtimeFileItems.length > 0 || images.length > 0) {
        attachmentBindings.setShowFilesDisplay(true)
      }

      if (images.length > 0) {
        attachmentBindings.setChatImages((previousImages) =>
          mergeRealtimeMessageImages(previousImages, images)
        )
      }
    },
    [
      chatId,
      getAttachmentStoreKey,
      getChatStoreKey,
      getScopedAttachmentBindings,
      getScopedChatBindings
    ]
  )

  const applyRealtimeMessageEvent = useCallback(
    (event: ChatThreadMessageRealtimeEvent) => {
      const normalizedChatId = chatId?.trim()

      if (!normalizedChatId) {
        return
      }

      const chatBindings = getScopedChatBindings(getChatStoreKey(normalizedChatId))

      if (event.eventType === 'DELETE') {
        if (event.chatId && event.chatId !== normalizedChatId) {
          return
        }

        const nextMessages = removeRealtimeChatMessage(
          chatBindings.getSnapshot().chatMessages,
          {
            chatId: event.chatId,
            messageId: event.messageId,
            sequenceNumber: event.sequenceNumber
          }
        )
        chatBindings.setChatMessages(nextMessages)
        syncPersistedAssistantTurnState(chatBindings, nextMessages)
        return
      }

      if (event.message.chat_id !== normalizedChatId) {
        return
      }

      const nextMessages = upsertRealtimeChatMessage(
        chatBindings.getSnapshot().chatMessages,
        event.message
      )
      chatBindings.setChatMessages(nextMessages)
      syncPersistedAssistantTurnState(chatBindings, nextMessages)

      void hydrateRealtimeMessageAssets(event.message)
    },
    [chatId, getChatStoreKey, getScopedChatBindings, hydrateRealtimeMessageAssets]
  )

  useChatThreadRealtime({
    chatId: chatId ?? null,
    enabled: chatRealtimeEnabled,
    onThreadChange: () => {
      if (!chatId) {
        return
      }

      if (visibleIsGenerating || visibleIsPersisting) {
        pendingRealtimeRefreshRef.current = true

        // Do NOT force-refresh on a timeout while generating. The forced
        // refresh reloads messages from the DB, which wipes in-memory
        // streaming content that hasn't been persisted yet — causing the
        // "stream clears and goes to loader" bug. Instead, just mark it
        // pending and let the reconciliation effect handle it when
        // isGenerating/isPersisting naturally clear.
        return
      }

      void loadOpenThread({
        forceFresh: true,
        showLoading: false,
        silent: true
      })
    },
    onMessageEvent: applyRealtimeMessageEvent
  })
  useActiveNotificationView({
    scopeType: 'chat',
    scopeId: chatId ?? null,
    enabled: Boolean(chatId)
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      !chatId ||
      visibleIsGenerating ||
      visibleIsPersisting ||
      !pendingRealtimeRefreshRef.current
    ) {
      return
    }

    pendingRealtimeRefreshRef.current = false

    if (pendingRealtimeRefreshTimeoutRef.current) {
      clearTimeout(pendingRealtimeRefreshTimeoutRef.current)
      pendingRealtimeRefreshTimeoutRef.current = null
    }

    void loadOpenThread({
      forceFresh: true,
      showLoading: false,
      silent: true
    })
  }, [chatId, visibleIsGenerating, visibleIsPersisting])

  useEffect(() => {
    return () => {
      if (pendingRealtimeRefreshTimeoutRef.current) {
        clearTimeout(pendingRealtimeRefreshTimeoutRef.current)
        pendingRealtimeRefreshTimeoutRef.current = null
      }
    }
  }, [])

  // Open existing chats already parked on the latest message — no top flash and
  // no smooth top→bottom animation. useLayoutEffect runs before paint; 'auto'
  // jumps; a short follow-up catches late layout (images / spacer measure).
  useLayoutEffect(() => {
    if (!chatId || highlightedMessageId) return
    if (!latestScrollRequest || latestScrollRequest.chatId !== chatId) return
    if (shouldShowLoading || !chatMessages || chatMessages.length === 0) return

    const requestNonce = latestScrollRequest.nonce
    setUserScrolled(false)
    scrollToBottom('auto')
    setIsAtBottom(true)

    const followUp = window.setTimeout(() => {
      scrollToBottom('auto')
      setIsAtBottom(true)
      setLatestScrollRequest((current) =>
        current?.chatId === chatId && current.nonce === requestNonce
          ? null
          : current
      )
    }, 80)

    return () => window.clearTimeout(followUp)
  }, [
    chatId,
    chatMessages,
    highlightedMessageId,
    latestScrollRequest,
    scrollToBottom,
    setIsAtBottom,
    setUserScrolled,
    shouldShowLoading
  ])

  useEffect(() => {
    if (!chatId || !shouldMarkOpenChatSeen) return
    if (!chatMessages || chatMessages.length === 0) return

    if (activeChatActivityStatus?.unread) {
      markChatActivitySeen(chatId, {
        requestId: activeChatActivityStatus.requestId
      })
    }

    if (activeSidecarStatus?.unread) {
      markSidecarChatSeen(chatId, {
        runId: activeSidecarStatus.runId
      })
    }
  }, [
    activeChatActivityStatus?.requestId,
    activeChatActivityStatus?.unread,
    activeSidecarStatus?.runId,
    activeSidecarStatus?.unread,
    chatId,
    chatMessages,
    markChatActivitySeen,
    markSidecarChatSeen,
    shouldMarkOpenChatSeen
  ])

  useEffect(() => {
    if (
      isPublic ||
      !chatId ||
      !surfaceSelectedChat?.is_group_chat ||
      !latestPersistedMessage?.id ||
      !shouldMarkOpenChatSeen
    ) {
      return
    }

    const requestKey = `${chatId}:${latestPersistedMessage.id}`

    if (groupReadStateRequestKeyRef.current === requestKey) {
      return
    }

    groupReadStateRequestKeyRef.current = requestKey

    void authenticatedFetch(
      `/api/chat/${encodeURIComponent(chatId)}/read-state`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messageId: latestPersistedMessage.id
        })
      }
    )
      .then((response) => {
        if (response.ok) {
          patchSidebarChatSnapshots(chatId, clearUnreadActivity)
          notifySidebarChatUnreadActivityCleared(chatId)
          setChats((currentChats) =>
            currentChats.map((chat) =>
              chat.id === chatId ? clearUnreadActivity(chat) : chat
            )
          )
        }
      })
      .catch(() => undefined)
  }, [
    chatId,
    isPublic,
    latestPersistedMessage?.id,
    setChats,
    shouldMarkOpenChatSeen,
    surfaceSelectedChat?.is_group_chat
  ])

  useHotkey('n', handleCreateChat)
  useHotkey('l', handleFocusChatInput)

  // Plan mode: "Build from plan" sends message with plan context
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ content: string }>).detail
      if (detail?.content) {
        handleSendMessageWithSidecarQueue(detail.content, chatMessages ?? [], false)
      }
    }
    window.addEventListener('buildFromPlan', handler)
    return () => window.removeEventListener('buildFromPlan', handler)
  }, [chatMessages, handleSendMessageWithSidecarQueue])

  // `loadOpenThread` intentionally stays local to this screen so the route
  // effect only keys off chat identity changes and URL-driven hydration.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (assistant) {
      handleSelectAssistant(assistant).catch(() => {})
    }

    setChatNotFound(false)
    pendingRealtimeRefreshRef.current = false

    if (!chatId) {
      chatLoadRequestRef.current += 1
      // When there's no chatId, we're at the main chat route showing empty state
      // Reset isNewChat to false to prevent multiple EmptyChatView renders
      setIsNewChat(false)
      setLoading(false)
      setIsTransitioning(false)
      return
    }

    if (isPublic) {
      chatLoadRequestRef.current += 1
      setIsNewChat(false)
      setLoading(false)
      setIsTransitioning(false)
      return
    }

    void loadOpenThread({
      forceFresh: true,
      showLoading: true,
      silent: false
    })
  }, [assistant, chatId, highlightedMessageId])

  useEffect(() => {
    handleSearchParams()
  }, [searchParams])

  async function loadOpenThread(
    options: {
      forceFresh?: boolean
      showLoading?: boolean
      silent?: boolean
    } = {}
  ) {
    if (!chatId) {
      return
    }

    const requestId = chatLoadRequestRef.current + 1
    chatLoadRequestRef.current = requestId
    const CHAT_OPEN_LOAD_TIMEOUT_MS = 25_000
    let loadTimeoutId: ReturnType<typeof window.setTimeout> | null = null

    const scopedChatSnapshot = getScopedChatBindings(getChatStoreKey(chatId)).getSnapshot()
    const hasScopedChatDataOnLoadStart =
      scopedChatSnapshot.chatMessages.length > 0 ||
      scopedChatSnapshot.chatFileItems.length > 0

    if (options.showLoading) {
      const alreadyHydrated = hydratedChatIdsRef.current.has(chatId) || hasScopedChatDataOnLoadStart
      setLoading(!alreadyHydrated)
      setIsTransitioning(!alreadyHydrated)
    }

    loadTimeoutId = window.setTimeout(() => {
      if (chatLoadRequestRef.current !== requestId) {
        return
      }

      setLoading(false)
      setIsTransitioning(false)

      if (!options.silent) {
        toast.error('This chat is taking longer than usual to load. Try refreshing the page.')
      }
    }, CHAT_OPEN_LOAD_TIMEOUT_MS)

    try {
      const loaded = await fetchChatData(requestId, {
        forceFresh: options.forceFresh
      })

      if (chatLoadRequestRef.current !== requestId) {
        return
      }

      if (!loaded) {
        setChatNotFound(true)
        setLoading(false)
        return
      }

      setLoading(false)
    } catch (error) {
      if (chatLoadRequestRef.current !== requestId) {
        return
      }

      if (isChatRouteAuthError(error)) {
        const currentPath =
          typeof window === 'undefined'
            ? pathname || '/chat'
            : `${window.location.pathname}${window.location.search}`

        if (!sessionExpiredRedirectRef.current) {
          sessionExpiredRedirectRef.current = true
          toast('Your session expired. Please sign in again.', {
            id: SESSION_EXPIRED_TOAST_ID
          })
          router.replace(buildLoginPath({ next: currentPath }))
        }

        setLoading(false)
        return
      }

      if (isChatRouteForbiddenError(error)) {
        toast.error('You no longer have access to this chat.')
        router.replace('/chat')
        setLoading(false)
        return
      }

      safeConsole.error('Failed to hydrate chat UI:', error)

      if (!options.silent) {
        toast.error(
          error instanceof Error && error.message.trim()
            ? error.message
            : "Couldn't load this chat. Refresh and try again."
        )
      }

      setLoading(false)
    } finally {
      if (loadTimeoutId !== null) {
        window.clearTimeout(loadTimeoutId)
      }

      if (chatLoadRequestRef.current !== requestId) {
        return
      }

      setIsTransitioning(false)
    }
  }

  const fetchChatData = async (
    requestId: number,
    options: {
      forceFresh?: boolean
    } = {}
  ): Promise<boolean> => {
    const resolvedChat = await resolveChatForLoad(requestId, {
      forceFresh: options.forceFresh
    })

    if (!resolvedChat) {
      return false
    }

    await Promise.all([
      fetchMessages(resolvedChat, requestId),
      fetchChat(resolvedChat, requestId)
    ])

    if (chatLoadRequestRef.current !== requestId) {
      return false
    }

    hydratedChatIdsRef.current.add(resolvedChat.id)

    // If we successfully loaded chat data, we're no longer in "new chat" mode
    if (chatId) {
      setIsNewChat(false)
    }

    sessionExpiredRedirectRef.current = false

    return true
  }

  const createRemixMessages = (
    filename: string,
    content: string,
    model: LLMID | null,
    assistantId: string | null
  ): ChatMessage[] => {
    return [
      {
        fileItems: [],
        message: {
          content: 'I created a new chat for you. You can edit this HTML file.',
          annotation: {} as any,
          assistant_id: assistantId || null,
          created_at: new Date().toISOString(),
          role: 'assistant',
          chat_id: chatId || '',
          id: '',
          image_paths: [],
          model: model || chatSettings?.model!,
          sequence_number: 0,
          updated_at: null,
          user_id: user?.id!,
          word_count: 0,
          metadata: null,
          type: null
        }
      },
      {
        fileItems: [],
        message: {
          content: `\`\`\`html
#filename=${filename}#
${content.replaceAll('```', '\\`\\`\\`')}
\`\`\``,
          annotation: {} as any,
          assistant_id: assistantId || null,
          created_at: new Date().toISOString(),
          role: 'assistant',
          chat_id: chatId || '',
          id: '',
          image_paths: [],
          model: model || chatSettings?.model!,
          sequence_number: 1,
          updated_at: null,
          user_id: user?.id!,
          word_count: 0,
          metadata: null,
          type: null
        }
      }
    ].map((m) => parseChatMessageCodeBlocksAndContent(m as any))
  }

  async function handleForkMessage(
    messageId: string,
    sequenceNo: number,
    assistantId: string | null,
    modelId: LLMID | null
  ) {
    const message = await getMessageById(messageId)
    if (message) {
      const codeBlock = parseDBMessageCodeBlocksAndContent({
        ...message,
        metadata: null,
        type: null
      })?.codeBlocks?.[sequenceNo]
      if (codeBlock && codeBlock.language === 'html' && codeBlock.filename) {
        await handleNewChat(
          '',
          createRemixMessages(codeBlock.filename, codeBlock.code, modelId, assistantId)
        )
      }
    }
  }

  function handleRemixFile(fileId: string, assistantId: string | null, modelId: LLMID | null) {
    getFileByHashId(fileId).then((file) => {
      if (chatMessages?.length === 0 && file && file.type === 'html') {
        setChatMessages(
          createRemixMessages(file.name, file.file_items[0].content, modelId, assistantId)
        )
      }
    })
  }

  const handleSearchParams = (): void => {
    const promptId = searchParams?.get('prompt_id')
    let modelId = searchParams?.get('model') as LLMID
    const remixFileId = searchParams?.get('remix')
    const forkMessageId = searchParams?.get('forkMessageId')
    const assistantId = searchParams?.get('assistant') || null
    const forkSequenceNo = parseInt(searchParams?.get('forkSequenceNo') || '-1')

    if (promptId) {
      getPromptById(parseIdFromSlug(promptId))
        .then((prompt) => {
          if (prompt) handleSelectPromptWithVariables(prompt)
        })
        .catch(() => {})
    }

    if (assistantId) {
      const assistant = assistants.find((assistant) => assistant.id === assistantId)
      if (assistant) {
        handleSelectAssistant(assistant).catch(() => {})
      }
    }

    if (modelId) {
      setChatSettings((prev) => ({ ...prev, model: modelId as LLMID }))
    }

    if (chatMessages?.length === 0 && remixFileId) {
      handleRemixFile(remixFileId, assistantId, modelId)
    }

    if (chatMessages?.length === 0 && forkMessageId && forkSequenceNo > -1) {
      handleForkMessage(forkMessageId, forkSequenceNo, assistantId, modelId).catch(() => {})
    }
  }

  const resolveChatForLoad = async (
    requestId: number,
    options: {
      forceFresh?: boolean
    } = {}
  ): Promise<Tables<'chats'> | null> => {
    if (isPublic) {
      return null
    }

    let chat = !options.forceFresh
      ? chats.find((candidate) => candidate.id === chatId)
      : undefined

    if (chatId && (!chat || options.forceFresh)) {
      const fetched = await fetchAccessibleChatById(chatId)
      if (chatLoadRequestRef.current !== requestId) {
        return null
      }
      if (fetched && !isPublic) {
        if (
          fetched.workspace_id &&
          fetched.workspace_id !== selectedWorkspace?.id &&
          !fetched.is_group_chat
        ) {
          await refreshAccessibleWorkspaces(fetched.workspace_id)

          if (chatLoadRequestRef.current !== requestId) {
            return null
          }
        }

        setChats((previousChats) => {
          const existingChat = previousChats.find((candidate) => candidate.id === fetched.id)

          if (!existingChat) {
            return [fetched, ...previousChats]
          }

          return previousChats.map((candidate) =>
            candidate.id === fetched.id ? fetched : candidate
          )
        })
        addChatToAtom({
          chat: fetched,
          viewerUserId: profile?.user_id ?? null
        })
      }
      chat = fetched ?? chat
    }

    return chat ?? null
  }

  const fetchMessages = async (
    chat: Tables<'chats'>,
    requestId: number
  ): Promise<void> => {
    if (chatLoadRequestRef.current !== requestId) {
      return
    }

    // Read one extra row so load-more can appear immediately on long chats.
    // If a highlighted message is requested, fetch all messages to ensure it exists in the list
    const fetchedMessages = await getMessagesByChatId(
      chat.id,
      highlightedMessageId ? undefined : INITIAL_MESSAGE_READ_LIMIT
    )

    if (chatLoadRequestRef.current !== requestId) {
      return
    }

    const mappedFetchedMessages = fetchedMessages.map((msg) => ({
      ...msg,
      metadata: msg.metadata ?? null,
      type: msg.type ?? null
    })).filter((msg) => {
      // Filter out soft-deleted messages
      const meta = msg.metadata as Record<string, unknown> | null
      return !meta?.deleted
    })

    const chatBindings = getScopedChatBindings(getChatStoreKey(chat.id))
    const currentScopedState = chatBindings.getSnapshot()
    const sortedMessages = sortChatMessagesByThreadOrder(
      mappedFetchedMessages.map(parseDBMessageCodeBlocksAndContent)
    )
    const uniqueFileItems = mappedFetchedMessages.flatMap((item) => item.file_items)
    const attachmentBindings = getScopedAttachmentBindings(
      getAttachmentStoreKey(chat.id)
    )

    // Apply the message list before slower attachment hydration so realtime
    // message updates are visible as soon as the thread refetch completes.
    const nextMessages = mergeFetchedMessagesWithVisibleMessages(
      sortedMessages,
      currentScopedState.chatMessages
    )
    chatBindings.setChatFileItems(uniqueFileItems)
    chatBindings.setChatMessages(nextMessages)
    syncPersistedAssistantTurnState(chatBindings, nextMessages)
    setUseRetrieval(true)
    attachmentBindings.setShowFilesDisplay(true)

    const [chatFiles, images] = await Promise.all([
      getChatFilesByChatId(chat.id).catch((error) => {
        safeConsole.warn('Failed to load chat files for chat UI:', error)
        return null
      }),
      fetchMessageImages(mappedFetchedMessages).catch((error) => {
        safeConsole.warn('Failed to load message images for chat UI:', error)
        return []
      })
    ])

    if (chatLoadRequestRef.current !== requestId) {
      return
    }

    attachmentBindings.setChatImages(images)
    attachmentBindings.setChatFiles(
      (chatFiles?.files ?? []).map((file: { id: any; name: any; type: any }) => ({
        id: file.id,
        name: file.name,
        type: file.type,
        file: null
      }))
    )
  }

  // useEffect(() => {
  //   if (chatMessages?.length > 0 && isGenerating) {
  //     const lastMessage = chatMessages[chatMessages.length - 1]
  //     const codeBlocks = lastMessage?.codeBlocks
  //     if (
  //       codeBlocks &&
  //       codeBlocks.length > 0 &&
  //       !!codeBlocks[codeBlocks.length - 1].filename
  //     ) {
  //       onSelectCodeBlock?.(codeBlocks[codeBlocks.length - 1])
  //     }
  //   } else {
  //     onSelectCodeBlock?.(null)
  //   }
  // }, [chatMessages])

  // const renderComponent = (component: any) => {
  //   switch (component.type) {
  //     case 'todo':
  //       return <TodoItem items={component.content} />
  //     case 'weather':
  //       return <WeatherData data={component.content} />
  //     case 'rain-chart':
  //       return <RainChart data={component.content} />
  //     case 'line-chart':
  //       return <HydraLineGraph {...component.content} />
  //     case 'pie-chart':
  //       return <HydraPieChart {...component.content} />
  //     // case 'code':
  //     //   return <CodeBlock language={component.language} content={component.content} />
  //     case 'text':
  //     default:
  //       return <p>{component.content}</p>
  //   }
  // }

  const fetchChat = async (
    chat: Tables<'chats'>,
    requestId: number
  ): Promise<void> => {
    if (chatLoadRequestRef.current !== requestId) {
      return
    }

    setSelectedChat(chat)

    if (chat.assistant_id) {
      const assistant = assistants.find((assistant) => assistant.id === chat.assistant_id)
      if (assistant) {
        setSelectedAssistant(assistant)
        const assistantTools = (await getAssistantToolsByAssistantId(assistant.id)).tools
        if (chatLoadRequestRef.current !== requestId) {
          return
        }
        setSelectedTools(assistantTools as unknown[] as Tables<'tools'>[])
      }
    }

    if (chatLoadRequestRef.current !== requestId) {
      return
    }

    // While a send is actively in flight for this chat, the DB row is a
    // stale snapshot by design — the model actually used only gets
    // persisted onto the chat row once the send finishes (see
    // touchChatActivity's call site in use-chat-handler.tsx, which runs
    // after generation completes). A background refresh that lands mid-send
    // (e.g. a long-running Sidecar task outliving the realtime-refresh
    // escape valve below) would otherwise overwrite the model the user just
    // picked with whatever the chat started with.
    const liveChatState = getScopedChatBindings(getChatStoreKey(chat.id)).getSnapshot()
    if (liveChatState.isGenerating || liveChatState.isPersisting) {
      return
    }

    setChatSettings((prevSettings) => ({
      ...prevSettings,
      // Only reset model from DB if user hasn't manually switched it
      // The touch endpoint persists model changes, but there's a race
      // between the persist and this refresh. Keep the user's selection.
      model: (prevSettings.model && prevSettings.model !== chat.model)
        ? prevSettings.model
        : chat.model as LLMID,
      prompt: chat.prompt,
      temperature: chat.temperature,
      contextLength: chat.context_length,
      includeProfileContext: chat.include_profile_context,
      includeWorkspaceInstructions: chat.include_workspace_instructions,
      embeddingsProvider: chat.embeddings_provider as 'openai' | 'local'
      // reflectMode: prevSettings.reflectMode
    }))
  }

  const isMobile = isMobileScreen()
  const isExperimentalCodeEditor = experimentalCodeEditor
  const model = allModels.find(
    (model) => model.modelId === chatSettings?.model || model.hostedId === chatSettings?.model
  )

  const { t } = useTranslation()
  const defaultChatTitle = t('chat_helper.title_generator.default_title')
  const surfaceChatTitle = surfaceSelectedChat?.name || defaultChatTitle
  const chatOverflowPayload = useMemo(() => {
    const plain = (sortedChatMessages ?? [])
      .map(entry => {
        const role = (entry.message.role || 'assistant').toUpperCase()
        const content =
          typeof entry.message.content === 'string' ? entry.message.content : ''
        return `${role}:\n${content}`
      })
      .join('\n\n')
    const markdown = [
      `# ${surfaceChatTitle}`,
      '',
      ...(sortedChatMessages ?? []).map(entry => {
        const role =
          (entry.message.role || 'assistant').toLowerCase() === 'user' ? 'You' : 'Losi'
        const content =
          typeof entry.message.content === 'string' ? entry.message.content : ''
        return `## ${role}\n\n${content}`
      })
    ].join('\n')
    return { title: surfaceChatTitle, plain, markdown }
  }, [sortedChatMessages, surfaceChatTitle])
  const desktopSurfaceChatTitle =
    surfaceChatTitle.length > 40
      ? `${surfaceChatTitle.substring(0, 40)}…`
      : surfaceChatTitle
  const handleDownloadGeneratedImages = useCallback(
    async (images: ImageStackImage[]) => {
      if (images.length === 0 || isDownloadingGeneratedImages) {
        return
      }

      setIsDownloadingGeneratedImages(true)

      try {
        const zip = await buildStoredAssetsZip(
          images.map((image, index) => ({
            fileName: `generated-image-${index + 1}`,
            fileType: 'image/png',
            url: `/api/image/download?url=${encodeURIComponent(image.url)}`
          }))
        )

        triggerBlobDownload(zip, `${surfaceChatTitle}-generated-images.zip`)
      } catch (error) {
        safeConsole.error('Failed to download generated image archive:', error)
        toast.error("Couldn't download those images. Try again.")
      } finally {
        setIsDownloadingGeneratedImages(false)
      }
    },
    [isDownloadingGeneratedImages, surfaceChatTitle]
  )

  const handleDownloadCreatedImages = useCallback(
    (artifacts: ChatCreatedArtifact[]) => {
      const images: ImageStackImage[] = artifacts
        .filter(
          (artifact): artifact is Extract<ChatCreatedArtifact, { kind: 'image' }> =>
            artifact.kind === 'image'
        )
        .map(artifact => ({
          id: artifact.id,
          url: artifact.url,
          alt: artifact.alt,
          name: artifact.title
        }))
      void handleDownloadGeneratedImages(images)
    },
    [handleDownloadGeneratedImages]
  )

  const handleOpenCreatedArtifact = useCallback(
    (artifact: ChatCreatedArtifact) => {
      if (artifact.kind === 'image') {
        setSelectedGeneratedImage({
          id: artifact.id,
          url: artifact.url,
          alt: artifact.alt,
          name: artifact.title
        })
        return
      }

      if (artifact.kind === 'plan') {
        window.dispatchEvent(
          new CustomEvent('openPlanInCanvas', {
            detail: {
              planId: artifact.planId,
              content: artifact.content,
              ...(artifact.status ? { status: artifact.status } : {})
            }
          })
        )
        return
      }

      if (artifact.kind === 'virtual_file') {
        window.dispatchEvent(
          new CustomEvent('openVirtualFileInCanvas', {
            cancelable: true,
            detail: {
              id: artifact.fileId,
              path: artifact.path,
              name: artifact.name
            }
          })
        )
        return
      }

      if (artifact.kind === 'pdf') {
        window.open(artifact.url, '_blank', 'noopener,noreferrer')
        return
      }

      if ('spaceItem' in artifact) {
        const href = getSidekickSpaceItemHref(artifact.spaceItem)
        if (isSplitMode) {
          navigateToHref(href)
          return
        }
        const opened = dispatchSidekickOpenSpaceItem(artifact.spaceItem)
        if (!opened) {
          navigateToHref(href)
        }
      }
    },
    [isSplitMode, navigateToHref]
  )

  // Replace the isLargeChat state with the one from the hook
  const { isLargeChat, messageTokenEstimate } = useChatMessages({ chatId })

  // Minimap: one entry per message for segment labels and scroll-to
  const minimapMessages: MinimapMessage[] = useMemo(() => {
    if (sortedChatMessages.length === 0) return []

    return sortedChatMessages.map((cm) => ({
      id: cm.message.id,
      role: (cm.message.role as 'user' | 'assistant' | 'system') || 'assistant',
      content: typeof cm.message.content === 'string' ? cm.message.content : undefined
    }))
  }, [sortedChatMessages])

  const crewChatRoster = useMemo(
    () => (chatId ? getCrewChatRosterForChat(chatId) : null),
    [chatId, sortedChatMessages.length]
  )
  const crewChatBoundMateId = useMemo(
    () => (chatId ? getCrewMemberForChat(chatId) : null),
    [chatId, crewChatRoster]
  )
  const crewChatSpeakingMateId = useMemo(() => {
    if (!crewChatRoster || !isGenerating) return null
    const last = sortedChatMessages[sortedChatMessages.length - 1]
    if (!last || last.message.role !== 'assistant') {
      return crewChatBoundMateId
    }
    const content =
      typeof last.message.content === 'string' ? last.message.content : ''
    return (
      parseCrewChatActiveSpeakerMateId(content, crewChatRoster) ??
      crewChatBoundMateId
    )
  }, [
    crewChatBoundMateId,
    crewChatRoster,
    isGenerating,
    sortedChatMessages
  ])
  const crewChatActiveMateId = isGenerating
    ? crewChatSpeakingMateId ?? crewChatBoundMateId
    : crewChatBoundMateId
  const crewChatPresenceStatuses = useMemo(() => {
    if (!crewChatRoster || !isGenerating) return undefined
    const thinkingId = crewChatSpeakingMateId ?? crewChatBoundMateId
    if (!thinkingId) return undefined
    return { [thinkingId]: 'thinking' as const }
  }, [
    crewChatBoundMateId,
    crewChatRoster,
    crewChatSpeakingMateId,
    isGenerating
  ])

  if (chatNotFound) {
    return notFound()
  }

  return (
    <div className="relative flex size-full flex-1 flex-col">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex size-full flex-1 flex-col overflow-hidden overflow-y-auto scroll-pt-[calc(7.5rem+env(safe-area-inset-top,0px))] md:scroll-pt-16"
      >
        <LanguageInitializer />
        {/* Header */}
        {isPublic ? (
          <PublicChatHeader
            authAction={publicHeaderAuthAction}
            onNewChat={onNewChat}
            showAuthActions={hasPublicAssistantResponse}
          />
        ) : (
          <div
            className={cn(
              'sticky top-0 z-20 flex h-auto w-full shrink-0 flex-col p-2 md:flex-row md:items-center md:justify-between',
              // Mobile: opaque sticky shell so pinned/sent turns cannot bleed
              // through the two-row title + actions bar.
              'bg-background md:bg-transparent',
              isSelecting ? 'md:min-h-14' : crewChatRoster ? 'md:min-h-14' : 'md:h-14'
            )}
          >
            {/* Mobile: solid opaque scrim — fade-bottom mask was letting pinned
                / sent turns bleed through the two-row title+actions bar.
                Desktop keeps the launch-ui fade blur. */}
            <div className="bg-background pointer-events-none absolute -top-2 bottom-0 left-0 right-0 z-0 md:hidden" />
            <div className="fade-bottom bg-background pointer-events-none absolute -top-2 bottom-0 left-0 right-0 z-0 hidden backdrop-blur-lg md:block" />
            {!isSelecting ? (
              <>
                {/* Mobile layout — two rows (title + actions). */}
                <div className="relative z-10 flex flex-col md:hidden">
                  {/* Chat name */}
                  <div className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center">
                    {!isPublic && (
                      <>
                        <div className="flex min-w-0 shrink-0 items-center">
                          {surfaceSelectedChat && (
                            <>
                              <StarredChatButton />
                              <ChatHighlightsButton
                                highlights={chatHighlights}
                                onDeleteHighlight={handleDeleteHighlight}
                              />
                            </>
                          )}
                        </div>
                        <h1 className="min-w-0 px-2 text-center font-bold">
                          <FadingChatTitle title={surfaceChatTitle} />
                          {isMentionOnlyGroupChat ? (
                            <span className="text-muted-foreground mt-1 block text-[10px] font-medium">
                              Mention @Losi to reply
                            </span>
                          ) : null}
                        </h1>
                        <div className="flex min-w-0 shrink-0 justify-end gap-1">
                          {surfaceSelectedChat?.is_group_chat ? (
                            <ChatGroupButton realtimeViewport="mobile" />
                          ) : canShareSurfaceChat ? (
                            <ShareChatButton />
                          ) : null}
                          {surfaceSelectedChat ? (
                            <ChatOverflowMenu
                              payload={chatOverflowPayload}
                              onComments={() => {
                                toast.message('Add a note for Losi in the composer, or Share to invite others.')
                              }}
                              onActivity={() => {
                                window.dispatchEvent(new Event('openCanvasBrowser'))
                              }}
                            />
                          ) : null}
                        </div>
                      </>
                    )}
                    {isPublic && (
                      <h1 className="min-w-0 flex-1 px-1 text-center font-bold">
                        <span className="block truncate">Losi Chat</span>
                      </h1>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center justify-between gap-2">
                    {/* Left side - Settings - Hidden for public chat */}
                    {!isPublic && (
                      <div className="flex items-center gap-2">
                        {showModelSelector && showChatSettings && <ChatSettings />}
                      </div>
                    )}

                    {/* Right side - Action Buttons */}
                    <div className="flex items-center gap-1">
                      {chatCreatedArtifacts.length > 0 ? (
                        <ChatCreatedStackPopover
                          artifacts={chatCreatedArtifacts}
                          onOpen={handleOpenCreatedArtifact}
                          onDownloadImages={handleDownloadCreatedImages}
                          disabled={isDownloadingGeneratedImages}
                        />
                      ) : null}
                      {!isPublic && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="z-50 md:hidden"
                          onClick={() => {
                            handleCreateChat()
                          }}
                          aria-label={t('chat_helper.title_generator.default_title')}
                        >
                          <IconMessagePlus {...iconProps} />
                        </Button>
                      )}

                      {/* Hamburger Menu - Hidden for public chat */}
                      {!isPublic && (
                        <>
                          {showSidebar ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="relative z-50 md:hidden"
                              onClick={() => setShowSidebar((prev) => !prev)}
                              aria-label="Toggle sidebar"
                            >
                              <AnimatePresence initial={false} mode="wait">
                                <motion.div
                                  className="absolute inset-0 flex items-center justify-center"
                                  key="collapse"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <IconLayoutSidebarLeftCollapse {...iconProps} />
                                </motion.div>
                              </AnimatePresence>
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="relative md:hidden"
                              onClick={() => setShowSidebar((prev) => !prev)}
                              aria-label="Toggle sidebar"
                            >
                              <AnimatePresence initial={false} mode="wait">
                                <motion.div
                                  className="absolute inset-0 flex items-center justify-center"
                                  key="expand"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <IconLayoutSidebarLeftExpandFilled {...iconProps} />
                                </motion.div>
                              </AnimatePresence>
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Desktop layout */}
                <div className="relative z-10 hidden size-full grid-cols-[minmax(0,1fr)_auto] items-center md:grid">
                  <div className="flex min-w-0 items-center justify-start pl-2">
                    {!isPublic && surfaceSelectedChat && (
                      <h1 className="min-w-0 truncate text-sm font-semibold leading-none">
                        <FadingChatTitle title={surfaceChatTitle} />
                      </h1>
                    )}
                    {!isPublic && isMentionOnlyGroupChat && (
                      <p className="text-muted-foreground ml-3 text-[11px] font-medium">
                        Mention @Losi to reply
                      </p>
                    )}
                    {isPublic && (
                      <h1 className="truncate px-4 text-sm font-semibold leading-none">
                        Losi Chat
                      </h1>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center justify-end gap-3">
                    {!isPublic && showModelSelector && (
                      <ChatSettings className="min-w-0 max-w-48 shrink" />
                    )}
                    {chatCreatedArtifacts.length > 0 ? (
                      <ChatCreatedStackPopover
                        artifacts={chatCreatedArtifacts}
                        onOpen={handleOpenCreatedArtifact}
                        onDownloadImages={handleDownloadCreatedImages}
                        disabled={isDownloadingGeneratedImages}
                      />
                    ) : null}
                    {!isPublic && surfaceSelectedChat ? (
                      <div className="flex shrink-0 items-center gap-1">
                        <StarredChatButton />
                        {canvasBrowserBetaEnabled ? (
                          <button
                            type="button"
                            onClick={() => window.dispatchEvent(new Event('openCanvasBrowser'))}
                            className="text-muted-foreground hover:text-foreground hover:bg-accent flex size-8 items-center justify-center rounded-md transition-colors"
                            title="Browser"
                            aria-label="Open Browser"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                          </button>
                        ) : null}
                        <ChatHighlightsButton
                          highlights={chatHighlights}
                          onDeleteHighlight={handleDeleteHighlight}
                        />
                        {surfaceSelectedChat.is_group_chat ? (
                          <ChatGroupButton realtimeViewport="desktop" />
                        ) : canShareSurfaceChat ? (
                          <ShareChatButton />
                        ) : null}
                        <ChatOverflowMenu
                          payload={chatOverflowPayload}
                          onComments={() => {
                            toast.message(
                              'Add a note for Losi in the composer, or Share to invite others.'
                            )
                          }}
                          onActivity={() => {
                            window.dispatchEvent(new Event('openCanvasBrowser'))
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            ) : (
              <ChatMessageSelector />
            )}
            {!isSelecting && crewChatRoster ? (
              <div className="relative z-10 mt-1 w-full px-1 md:px-2">
                <CrewMemberChrome
                  activeMateId={crewChatActiveMateId}
                  activityByMateId={crewChatPresenceStatuses}
                  roster={crewChatRoster}
                  variant="header"
                />
              </div>
            ) : null}
          </div>
        )}

        {selectedGeneratedImage ? (
          <ImageModal
            isOpen={Boolean(selectedGeneratedImage)}
            onClose={() => setSelectedGeneratedImage(null)}
            src={selectedGeneratedImage.url}
            alt={selectedGeneratedImage.alt}
          />
        ) : null}

        {/* Chat Content */}
        <LayoutGroup id={composerLayoutGroupId}>
        <div className="flex size-full">
          {shouldShowLoading ? (
            <Loading withMessage={true} />
          ) : (
            <div
              className={cn(
                'relative mx-auto flex size-full max-w-4xl flex-1 flex-col',
                isPublic ? 'pt-6' : ''
              )}
            >
              {/* Show messages view if we have messages OR are generating */}
              {(chatMessages && chatMessages.length > 0) || isGenerating ? (
                <>
                  <div ref={messagesStartRef} />

                  {/* Show message token count for large chats */}
                  {isLargeChat && (
                    <div className="text-muted-foreground px-4 py-2 text-xs">
                      <span>
                        Approximately {messageTokenEstimate.toLocaleString()} tokens in this
                        conversation
                      </span>
                    </div>
                  )}

                  {/* Use virtualized list for all large chats */}
                  <div className="mx-auto w-full max-w-2xl">
                    <ChatMessages
                      onSelectCodeBlock={onSelectCodeBlock}
                      onSelectMessage={onSelectMessage}
                      isExperimentalCodeEditor={isExperimentalCodeEditor}
                      isPublic={isPublic}
                      chatId={chatId}
                      chatHighlights={chatHighlights}
                      onCreateHighlight={handleCreateHighlight}
                      pinNewestMessageToTop={
                        isPublic ? undefined : pinElementToTop
                      }
                    />
                    {isPublic && publicInlineSaveCta ? (
                      <div className="px-2 pb-3 pt-4 sm:px-0">{publicInlineSaveCta}</div>
                    ) : null}
                  </div>

                  <div
                    ref={messagesEndRef}
                    // No flex-1: growing the spacer to fill leftover column height
                    // after a short reply leaves a huge empty band under the last
                    // message. Idle (last turn is assistant): composer-height only.
                    // From user-send through generation: 65dvh headroom so the
                    // new user turn can pin near the top. Shrinking when the
                    // reply finishes collapses the void scroll-to-bottom used
                    // to leave on screen forever.
                    style={{
                      minHeight: (() => {
                        const composerPad = Math.max(180, inputContainerHeight)
                        if (isPublic) return composerPad
                        const lastRole =
                          sortedChatMessages[sortedChatMessages.length - 1]
                            ?.message.role
                        const needsPinHeadroom =
                          isGenerating || lastRole === 'user'
                        return needsPinHeadroom
                          ? `max(${composerPad}px, 65dvh)`
                          : composerPad
                      })()
                    }}
                  />
                </>
              ) : // Show EmptyChatView only when we have no messages, not transitioning, and not generating
              !isTransitioning ? (
                <EmptyChatView
                  chatId={chatId}
                  model={model!}
                  selectedAssistant={selectedAssistant}
                  assistants={assistants || []}
                  theme={theme}
                  profile={profile}
                  showAssistantSelector={showAssistantSelector}
                  handleSendMessage={handleSendMessageWithSidecarQueue}
                  handleStopMessage={handleStopMessage}
                  chatMessages={chatMessages || []}
                  isPublic={isPublic}
                  realtimeEnabled={chatRealtimeEnabled}
                  composerLayoutId={composerLayoutId}
                  handleSelectPromptWithVariables={handleSelectPromptWithVariables}
                  onPlanModeClick={onPlanModeClick}
                  hasPlan={hasPlan}
                  canFocusComposer={canFocusComposer}
                  onProductCreationCommand={onProductCreationCommand}
                  sidecarRunActive={sidecarRunActive}
                  sidecarQueueCount={effectiveQueuedSidecarTurns.length}
                  queuedSidecarTurns={effectiveQueuedSidecarTurns}
                  onRemoveQueuedTurn={(id) =>
                    effectiveSetQueuedSidecarTurns((prev) => prev.filter((t) => t.id !== id))
                  }
                  onUpdateQueuedTurn={(id, message) =>
                    effectiveSetQueuedSidecarTurns((prev) =>
                      prev.map((t) => (t.id === id ? { ...t, message } : t))
                    )
                  }
                  onSendQueuedTurnNow={sendQueuedTurnNow}
                />
              ) : null}
              {/* Bottom Chat Input - Only show when there are messages or generating */}
              {(chatMessages && chatMessages.length > 0) || isGenerating ? (
                <AnimatePresence mode="wait">
                  <motion.div
                    key="bottom-chat-composer"
                    className="pointer-events-none sticky bottom-0 w-full"
                    layout="position"
                    initial={false}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 360, damping: 42, mass: 0.8 }}
                    style={{ zIndex: 19 }}
                  >
                    <div className="from-background/60 pointer-events-none absolute inset-0 rounded-t-[2rem] bg-gradient-to-t to-transparent" />
                    <div
                      ref={inputContainerRef}
                      className="pointer-events-none relative min-h-[100px] px-2 pb-[max(1.5rem,calc(env(safe-area-inset-bottom)+0.75rem))]"
                    >
                      <div className="pointer-events-auto relative mx-auto w-full max-w-2xl">
                        {/* Height sentinel only — suggestion links stay inline under the bubble */}
                        <div ref={responseControlsRef} className="hidden" aria-hidden />
                        <ChatInput
                          chatId={chatId}
                          showAssistant={showAssistantSelector && !selectedAssistant}
                          handleSendMessage={handleSendMessageWithSidecarQueue}
                          handleStopMessage={handleStopMessage}
                          isPublic={isPublic}
                          realtimeEnabled={chatRealtimeEnabled}
                          composerLayoutId={composerLayoutId}
                          forceDraftChat={forceDraftChat && !chatId}
                          onPlanModeClick={onPlanModeClick}
                          hasPlan={hasPlan}
                          canFocusComposer={canFocusComposer}
                          onProductCreationCommand={onProductCreationCommand}
                          sidecarRunActive={sidecarRunActive}
                          sidecarQueueCount={effectiveQueuedSidecarTurns.length}
                          queuedSidecarTurns={effectiveQueuedSidecarTurns}
                          onRemoveQueuedTurn={(id) =>
                            effectiveSetQueuedSidecarTurns((prev) => prev.filter((t) => t.id !== id))
                          }
                          onUpdateQueuedTurn={(id, message) =>
                            effectiveSetQueuedSidecarTurns((prev) =>
                              prev.map((t) => (t.id === id ? { ...t, message } : t))
                            )
                          }
                          onSendQueuedTurnNow={sendQueuedTurnNow}
                        />
                        <ChatLongMessageNotice />
                        <ChatMessageCounter />
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              ) : null}
            </div>
          )}
        </div>
        </LayoutGroup>

        <AnimatePresence mode="wait">
          {shouldShowScrollToBottomButton ? (
            <motion.div
              key="scroll-to-bottom-overlay"
              className="pointer-events-none absolute inset-x-0 z-30 flex justify-center"
              initial={{ opacity: 0, scale: 0.86 }}
              animate={{
                opacity: 1,
                scale: 1,
                bottom: inputContainerHeight + 24
              }}
              exit={{ opacity: 0, scale: 0.86 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              <Button
                size="icon"
                variant="outline"
                aria-label="Scroll to latest message"
                className="bg-background/90 pointer-events-auto size-9 rounded-full shadow-md backdrop-blur transition-all duration-200"
                onClick={() => scrollToBottom()}
              >
                <IconArrowDown size={20} stroke={2} />
              </Button>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* <div
            className={cn(
              "w-0 transition-[width] duration-100",
              editorOpen && "w-full lg:w-[calc(50%-2rem)]"
            )}
          /> */}

        {/* <ChatPreviewContent
            open={editorOpen}
            isGenerating={isGenerating}
            selectedCodeBlock={selectedCodeBlock}
            onSelectCodeBlock={handleSelectCodeBlock}
            isEditable={isCodeBlockEditable(selectedCodeBlock)}
            onCodeChange={handleCodeChange}
          /> */}
        {/* </div> */}
      </div>

      {/* <div className="absolute bottom-4 right-4 hidden md:block lg:bottom-6 lg:right-6">
        <ChatHelp />
      </div> */}
      {/* <div className="fixed bottom-24 right-5 z-10">
        <ChatScrollButtons
          isAtTop={isAtTop}
          isAtBottom={isAtBottom}
          isOverflowing={isOverflowing}
          scrollToTop={scrollToTop}
          scrollToBottom={scrollToBottom}
        />
      </div> */}
      {((chatMessages && chatMessages.length > 0) || isGenerating) && (
        <ChatMessagesMinimap
          scrollContainerRef={scrollRef}
          messages={minimapMessages}
          visible={minimapVisible}
          onRequestClose={() => setMinimapVisible(false)}
        />
      )}
      {sidecarSystemEnabled && effectiveLatestRun && !sidecarCanvas && (
        <Button
          type="button"
          aria-label="Open Sidecar run details"
          onClick={() => {
            onRequestSidecarFocus?.()
            effectiveSelectRun(effectiveLatestRun.runId, { manual: false })
            effectiveSetPanelOpen(true)
            handleFocusChatInput()
          }}
          className="bg-slate-950/92 fixed right-6 top-24 z-40 h-auto rounded-full border border-white/10 px-4 py-2 text-left text-white shadow-2xl shadow-black/30 hover:bg-slate-900"
        >
          <span className="flex items-center gap-2 text-sm font-semibold">
            Sidecar
            <span className="text-xs font-normal text-white/65">
              {effectiveLatestRun.status === 'running' ||
              effectiveLatestRun.status === 'planning'
                ? 'Running'
                : effectiveRuns.length > 1
                  ? `View ${effectiveRuns.length} runs`
                  : 'View run'}
            </span>
          </span>
        </Button>
      )}
      {/* Sidecar is canvas-only: opening the panel opens the shared preview; no floating sheet */}
    </div>
  )
}

interface EmptyChatViewProps {
  chatId?: string
  model: LLM
  selectedAssistant: Tables<'assistants'> | null
  assistants?: Tables<'assistants'>[]
  theme: string | undefined
  profile: Tables<'profiles'> | null
  showAssistantSelector: boolean
  handleSendMessage: (
    message: string,
    chatMessages: ChatMessage[],
    isUserMessage: boolean,
    options?: ChatSendOptions
  ) => void | Promise<void>
  handleStopMessage: (options?: ChatStopOptions) => void
  chatMessages: ChatMessage[]
  isPublic?: boolean
  realtimeEnabled?: boolean
  composerLayoutId?: string
  handleSelectPromptWithVariables?: (prompt: Tables<'prompts'>) => void
  onPlanModeClick?: (messageContent: string) => void
  hasPlan?: boolean
  canFocusComposer?: boolean
  onProductCreationCommand?: (command: ChatProductCreationCommand) => void
  sidecarRunActive?: boolean
  sidecarQueueCount?: number
  queuedSidecarTurns?: QueuedSidecarTurn[]
  onRemoveQueuedTurn?: (id: string) => void
  onUpdateQueuedTurn?: (id: string, message: string) => void
  onSendQueuedTurnNow?: (id: string) => void
}

const EmptyChatView: React.FC<EmptyChatViewProps> = memo(
  ({
    chatId,
    selectedAssistant,
    model,
    assistants,
    theme,
    profile,
    showAssistantSelector,
    handleSendMessage,
    handleStopMessage,
    chatMessages,
    isPublic = false,
    realtimeEnabled = false,
    composerLayoutId,
    handleSelectPromptWithVariables,
    onPlanModeClick,
    hasPlan = false,
    canFocusComposer = true,
    onProductCreationCommand,
    sidecarRunActive = false,
    sidecarQueueCount = 0,
    queuedSidecarTurns = [],
    onRemoveQueuedTurn,
    onUpdateQueuedTurn,
    onSendQueuedTurnNow
  }) => {
    const { t } = useTranslation()
    const { track } = useAnalytics()
    const setUserInput = useScopedSetUserInput()
    const [widgetState, setWidgetState] = useState<EmptyChatWidgetsState>(
      DEFAULT_EMPTY_CHAT_WIDGETS_STATE
    )
    const [customizeOpen, setCustomizeOpen] = useState(false)
    const [nexusWidgetAccess, setNexusWidgetAccess] = useState<boolean | null>(null)

    const { user } = useAuth()
    const { selectedWorkspace, chats, prompts } = useContext(ChatbotUIContext)
    const { isNewChat } = useContext(ChatbotUIChatContext)

    useEffect(() => {
      setWidgetState(getEmptyChatWidgetsState())
    }, [])

    useEffect(() => {
      if (isPublic || !user?.id || !selectedWorkspace?.id) {
        setNexusWidgetAccess(false)
        return
      }

      let cancelled = false
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 8000)
      setNexusWidgetAccess(null)

      authenticatedFetch(
        `/api/workspaces/${encodeURIComponent(selectedWorkspace.id)}/check-permission?nexus_area=overview&nexus_level=view&include_nexus_signal=false`,
        {
          cache: 'no-store',
          signal: controller.signal
        }
      )
        .then((response) => (response.ok ? response.json() : { hasPermission: false }))
        .then((data: { hasPermission?: boolean }) => {
          if (!cancelled) setNexusWidgetAccess(data.hasPermission === true)
        })
        .catch(() => {
          if (!cancelled) setNexusWidgetAccess(false)
        })
        .finally(() => clearTimeout(timeout))

      return () => {
        cancelled = true
        clearTimeout(timeout)
        controller.abort()
      }
    }, [isPublic, selectedWorkspace?.id, user?.id])

    useEffect(() => {
      if (nexusWidgetAccess !== false) return

      setWidgetState((current) => {
        const next = filterEmptyChatWidgetsForNexusAccess(current, false)
        if (next.enabledWidgets.length === current.enabledWidgets.length) return current

        setEmptyChatWidgetsState(next)
        return next
      })
    }, [nexusWidgetAccess])

    // Load from API when signed in (server as source of truth across devices)
    useEffect(() => {
      if (!user?.id) return
      let cancelled = false
      authenticatedFetch('/api/user/empty-chat-widgets', {
        cache: 'no-store'
      })
        .then((res) => res.json())
        .then((data: { state: unknown }) => {
          if (cancelled) return
          const parsed = parseEmptyChatWidgetsStateFromApi(data?.state)
          if (parsed) {
            const nextState = {
              ...DEFAULT_EMPTY_CHAT_WIDGETS_STATE,
              ...parsed,
              enabledWidgets: parsed.enabledWidgets.length
                ? parsed.enabledWidgets
                : DEFAULT_EMPTY_CHAT_WIDGETS_STATE.enabledWidgets
            }

            setWidgetState(
              nexusWidgetAccess === false
                ? filterEmptyChatWidgetsForNexusAccess(nextState, false)
                : nextState
            )
          }
        })
        .catch(() => {})
      return () => {
        cancelled = true
      }
    }, [nexusWidgetAccess, user?.id])

    // Debounced save to API when signed in
    const saveToServerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(() => {
      if (!user?.id) return
      if (nexusWidgetAccess === null) return

      if (saveToServerRef.current) clearTimeout(saveToServerRef.current)
      saveToServerRef.current = setTimeout(() => {
        saveToServerRef.current = null
        const safeWidgetState = filterEmptyChatWidgetsForNexusAccess(
          widgetState,
          nexusWidgetAccess
        )

        authenticatedFetch('/api/user/empty-chat-widgets', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ state: safeWidgetState })
        }).catch(() => {})
      }, 1200)
      return () => {
        if (saveToServerRef.current) clearTimeout(saveToServerRef.current)
      }
    }, [nexusWidgetAccess, user?.id, widgetState])

    // Get combined conversation starters (personalized + trending, or all trending for public)
    const {
      starters: combinedStarters,
      structuredStarters,
      isLoading: startersLoading,
      metadata
    } = useCombinedConversationStarters({
      userId: user?.id,
      workspaceId: selectedWorkspace?.id,
      selectedAssistant,
      // Interest-only starters — never mix trending/news into the strip.
      includeTriending: false,
      allTrending: false,
      maxStarters: 8,
      enabled: true,
      forceRefresh: isNewChat
    })

    const handleStarterSelect = useCallback(
      (value: string) => {
        if (isPublic) {
          const selectedStarter = structuredStarters.find(
            (starter) => starter.input === value
          )

          void trackPublicChatStarterPromptClicked({
            surface: 'public_chat',
            prompt_type: selectedStarter?.isTrending ? 'trending' : 'starter',
            is_anonymous: true
          })

          track('public_chat_starter_selected', {
            surface: 'try_empty_state',
            promptSource: structuredStarters.length > 0 ? 'dynamic' : 'fallback',
            promptCount:
              structuredStarters.length ||
              combinedStarters?.length ||
              DEFAULT_CONVERSATION_STARTERS.length
          })
        }

        setUserInput(value)
      },
      [combinedStarters?.length, isPublic, setUserInput, structuredStarters, track]
    )

    return (
      <div className="center flex w-full flex-1 flex-col items-center justify-center transition-[height]">
        <LanguageInitializer />
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-3 sm:px-6 md:px-8">
          {!selectedAssistant ? (
            <Brand
              theme={theme === 'dark' ? 'dark' : 'light'}
              userName={profile?.display_name}
              loading={!model}
              loadAnimation={false}
            />
          ) : (
            <>
              <AssistantIcon className="rounded-full" assistant={selectedAssistant!} size={80} />
              <div className="text-foreground mt-2 text-center text-2xl font-semibold">
                {t(selectedAssistant!.name)}
              </div>
              <div className="text-foreground mt-1 text-center text-sm opacity-80">
                {t(selectedAssistant!.description.split('.')[0])}
              </div>
            </>
          )}
        </div>

        {/* Chat Input + widgets: slide up when customize sheet opens */}
        <motion.div
          className="mt-6 w-full px-3 sm:px-6 md:px-8"
          initial={false}
          animate={{
            opacity: 1,
            y: customizeOpen ? -160 : 0
          }}
          transition={{ type: 'tween', duration: 0.3, ease: 'easeOut' }}
        >
          <div className="mx-auto w-full max-w-3xl">
            <ChatInput
              chatId={chatId}
              showAssistant={showAssistantSelector && !selectedAssistant}
              handleSendMessage={handleSendMessage}
              handleStopMessage={handleStopMessage}
              isPublic={isPublic}
              realtimeEnabled={realtimeEnabled}
              composerLayoutId={composerLayoutId}
              forceDraftChat={!chatId}
              showWebBrowseToggle={isPublic}
              onPlanModeClick={onPlanModeClick}
              hasPlan={hasPlan}
              canFocusComposer={canFocusComposer}
              onProductCreationCommand={onProductCreationCommand}
              sidecarRunActive={sidecarRunActive}
              sidecarQueueCount={sidecarQueueCount}
              queuedSidecarTurns={queuedSidecarTurns}
              onRemoveQueuedTurn={onRemoveQueuedTurn}
              onUpdateQueuedTurn={onUpdateQueuedTurn}
              onSendQueuedTurnNow={onSendQueuedTurnNow}
            />

            {/* Optional Trending (conversation starters) - full-width, visibility from Customize */}
            {widgetState.showTrending && (
              <div className="mt-6 flex min-h-[140px] w-full items-start">
                {!startersLoading ? (
                  <ConversationStarters
                    values={
                      selectedAssistant?.conversation_starters ||
                      combinedStarters ||
                      DEFAULT_CONVERSATION_STARTERS
                    }
                    starters={structuredStarters}
                    onSelect={handleStarterSelect}
                    metadata={metadata}
                  />
                ) : (
                  <div className="grid min-h-[120px] w-full grid-cols-1 items-center justify-center gap-3 sm:grid-cols-2">
                    <div className="bg-muted/30 h-12 animate-pulse rounded-xl" />
                    <div className="bg-muted/30 h-12 animate-pulse rounded-xl" />
                    <div className="bg-muted/30 h-12 animate-pulse rounded-xl" />
                    <div className="bg-muted/30 h-12 animate-pulse rounded-xl" />
                  </div>
                )}
              </div>
            )}

            {!isPublic && profile ? (
              <div className="mt-6 flex w-full flex-col gap-4">
                <OnboardingChecklist />
              </div>
            ) : null}

            {/* Widget grid and Customize bar - temporarily hidden (WIP) */}
            <div className="hidden" aria-hidden="true">
              <EmptyChatWidgetsSection
                widgetState={widgetState}
                onWidgetStateChange={setWidgetState}
                chats={chats ?? []}
                prompts={prompts ?? []}
                setUserInput={setUserInput}
                onSendMessage={(text) =>
                  handleSendMessage(text, chatMessages ?? [], false)
                }
                onSelectPrompt={handleSelectPromptWithVariables}
                customizeOpen={customizeOpen}
                onCustomizeOpenChange={setCustomizeOpen}
                hasNexusAccess={nexusWidgetAccess === true}
              />
            </div>
          </div>
        </motion.div>
      </div>
    )
  }
)

EmptyChatView.displayName = 'EmptyChatView'
